import { _ as __decorate, a as __metadata } from '../tslib.es6-Cv888lzs.js';
import { directive, Directive } from 'lit/directive.js';
import { LitElement, css, html } from 'lit';
import { state, query, property, queryAssignedElements, customElement } from 'lit/decorators.js';
import { live } from 'lit/directives/live.js';
import { repeat } from 'lit/directives/repeat.js';

const ARIA_IDREF_ATTRIBUTES = new Map([
    ['aria-activedescendant', 'ariaActivedescendantElement'],
    ['aria-controls', 'ariaControlsElements'],
    ['aria-describedby', 'ariaDescribedByElements'],
    ['aria-details', 'ariaDetailsElements'],
    ['aria-errormessage', 'ariaErrormessageElements'],
    ['aria-flowto', 'ariaFlowtoElements'],
    ['aria-labelledby', 'ariaLabelledByElements'],
    ['aria-owns', 'ariaOwnsElements'],
]);
function resolveAriaIdRefs(element, value) {
    const ids = value
        .trim()
        .split(/\s+/)
        .filter((id) => id.length > 0);
    if (ids.length === 0)
        return null;
    const root = element.getRootNode();
    let resolvedElements = [];
    if (root instanceof ShadowRoot) {
        resolvedElements = ids.map((id) => root.getElementById(id));
    }
    else if (root instanceof Document) {
        resolvedElements = ids.map((id) => root.getElementById(id));
    }
    else {
        resolvedElements = ids.map((id) => document.getElementById(id));
    }
    if (resolvedElements.filter((el) => el !== null).length === 0) {
        resolvedElements = ids.map((id) => document.getElementById(id));
    }
    const validElements = resolvedElements.filter((el) => el !== null);
    return validElements.length === ids.length ? validElements : null;
}
function setInternalsProperty(el, name, resolvedElements) {
    if (name === 'aria-activedescendant') {
        const [firstElement] = resolvedElements;
        // eslint-disable-next-line no-param-reassign
        el[name] = firstElement;
    }
    else {
        // eslint-disable-next-line no-param-reassign
        el[name] = resolvedElements;
    }
}
class SetAttributesDirective extends Directive {
    update(part, [attributes]) {
        const el = part.element;
        const internals = el.internals ?? null;
        for (const [name, value] of Object.entries(attributes)) {
            if (value == null) {
                el.removeAttribute(name);
            }
            else {
                const internalsPropertyName = ARIA_IDREF_ATTRIBUTES.get(name);
                if (internalsPropertyName) {
                    const resolvedElements = resolveAriaIdRefs(el, String(value));
                    if (resolvedElements) {
                        if (internals) {
                            // Set on ElementInternals
                            setInternalsProperty(internals, internalsPropertyName, resolvedElements);
                        }
                        else {
                            // Set internals property on shadowdom item
                            el.setAttribute(name, String(value));
                            setInternalsProperty(el, internalsPropertyName, resolvedElements);
                        }
                    }
                }
                else {
                    // Non-ARIA IDREF attributes: set normally
                    el.setAttribute(name, String(value));
                }
            }
        }
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    render(_attributes) {
        return null;
    }
}
/* How to use:

<textarea ${setAttributes(this._ariaAttributes)} ... />
*/
directive(SetAttributesDirective);

function r(e){var t,f,n="";if("string"==typeof e||"number"==typeof e)n+=e;else if("object"==typeof e)if(Array.isArray(e)){var o=e.length;for(t=0;t<o;t++)e[t]&&(f=r(e[t]))&&(n&&(n+=" "),n+=f);}else for(f in e)e[f]&&(n&&(n+=" "),n+=f);return n}function clsx(){for(var e,t,f=0,n="",o=arguments.length;f<o;f++)(e=arguments[f])&&(t=r(e))&&(n&&(n+=" "),n+=t);return n}

const connectedElements = new Set();
if (window && !window.translations) {
    window.translations = new Map();
}
const { translations } = window;
let fallback;
// TODO: We need some way for users to be able to set these on the server.
let documentDirection = 'ltr';
// Fallback for server.
let documentLanguage = 'en';
const isClient = typeof MutationObserver !== 'undefined' &&
    typeof document !== 'undefined' &&
    typeof document.documentElement !== 'undefined';
/** Updates all localized elements that are currently connected */
function update() {
    if (isClient) {
        documentDirection = document.documentElement.dir || 'ltr';
        documentLanguage = document.documentElement.lang || navigator.language;
    }
    [...connectedElements.keys()].forEach((el) => {
        const litEl = el;
        if (typeof litEl.requestUpdate === 'function') {
            litEl.requestUpdate();
        }
    });
}
if (isClient) {
    const documentElementObserver = new MutationObserver(update);
    documentDirection = document.documentElement.dir || 'ltr';
    documentLanguage = document.documentElement.lang || navigator.language;
    // Watch for changes on <html lang>
    documentElementObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['dir', 'lang'],
    });
}
/** Registers one or more translations */
function registerTranslation(...translation) {
    translation.forEach((t) => {
        const code = t.$code.toLowerCase();
        if (translations.has(code)) {
            // Merge translations that share the same language code
            translations.set(code, { ...translations.get(code), ...t });
        }
        else {
            translations.set(code, t);
        }
        // The first translation that's registered is the fallback
        if (!fallback) {
            fallback = t;
        }
    });
    update();
}
if (typeof window !== 'undefined') {
    window.registerTranslation = registerTranslation;
}
/**
 * Localize Reactive Controller for components built with Lit
 *
 * To use this controller, import the class and instantiate it in a custom element constructor:
 *
 * private localize = new LocalizeController(this);
 *
 * This will add the element to the set and make it respond to changes to <html dir|lang> automatically. To make it
 * respond to changes to its own dir|lang properties, make it a property:
 *
 *   @property() dir: string;
 *   @property() lang: string;
 *
 * To use a translation method, call it like this:
 *
 *   ${this.localize.term('term_key_here')}
 *   ${this.localize.date('2021-12-03')}
 *   ${this.localize.number(1000000)}
 */
class LocalizeController {
    constructor(host) {
        this.host = host;
        this.host.addController(this);
    }
    hostConnected() {
        connectedElements.add(this.host);
    }
    hostDisconnected() {
        connectedElements.delete(this.host);
    }
    /**
     * Gets the host element's directionality as determined by the `dir` attribute. The return value is transformed to
     * lowercase.
     */
    dir() {
        return `${this.host.dir || documentDirection}`.toLowerCase();
    }
    /**
     * Gets the host element's language as determined by the `lang` attribute. The return value is transformed to
     * lowercase.
     */
    lang() {
        return `${this.host.lang || documentLanguage}`.toLowerCase();
    }
    // eslint-disable-next-line class-methods-use-this
    getTranslationData(lang) {
        // Convert "en_US" to "en-US". Note that both underscores and dashes are allowed per spec, but underscores result in
        // a RangeError by the call to `new Intl.Locale()`. See: https://unicode.org/reports/tr35/#unicode-locale-identifier
        const locale = new Intl.Locale(lang.replace(/_/g, '-'));
        const language = locale?.language.toLowerCase();
        const region = locale?.region?.toLowerCase() ?? '';
        const primary = translations.get(`${language}-${region}`);
        const secondary = translations.get(language);
        return { locale, language, region, primary, secondary };
    }
    /** Determines if the specified term exists, optionally checking the fallback translation. */
    exists(key, options) {
        const { primary, secondary } = this.getTranslationData(options.lang ?? this.lang());
        const mergedOptions = {
            includeFallback: false,
            ...options,
        };
        if ((primary && primary[key]) ||
            (secondary && secondary[key]) ||
            (mergedOptions.includeFallback && fallback && fallback[key])) {
            return true;
        }
        return false;
    }
    /** Outputs a translated term. */
    term(key, ...args) {
        const { primary, secondary } = this.getTranslationData(this.lang());
        let term;
        // Look for a matching term using regionCode, code, then the fallback
        if (primary && primary[key]) {
            term = primary[key];
        }
        else if (secondary && secondary[key]) {
            term = secondary[key];
        }
        else if (fallback && fallback[key]) {
            term = fallback[key];
        }
        else {
            // eslint-disable-next-line no-console
            console.error(`No translation found for: ${String(key)}`);
            return String(key);
        }
        if (typeof term === 'function') {
            return term(...args);
        }
        return term;
    }
    /** Outputs a localized date in the specified format. */
    date(dateToFormat, options) {
        const date = new Date(dateToFormat);
        return new Intl.DateTimeFormat(this.lang(), options).format(date);
    }
    /** Outputs a localized number in the specified format. */
    number(numberToFormat, options) {
        const num = Number(numberToFormat);
        return Number.isNaN(num) ? '' : new Intl.NumberFormat(this.lang(), options).format(num);
    }
    /** Outputs a localized time in relative format. */
    relativeTime(value, unit, options) {
        return new Intl.RelativeTimeFormat(this.lang(), options).format(value, unit);
    }
}

/**
 * @param Base The base class.
 * @returns A mix-in implementing `localizations` method.
 *
 *@example
 *    <!-- Terms -->
      ${this.$localize.term('hello')}
      or
      ${this.$t('hello')}

      <!-- Dates -->
      ${this.$localize.date('2021-09-15 14:00:00 ET', { month: 'long', day: 'numeric', year: 'numeric' })}
      or
      ${this.$d('2021-09-15 14:00:00 ET', { month: 'long', day: 'numeric', year: 'numeric' })}

      <!-- Numbers/currency -->
      ${this.$localize.number(1000, { style: 'currency', currency: 'USD'})}
      or
      ${this.$n(1000,{ style: 'currency', currency: 'USD'})}

      <!-- Determining language -->
      ${this.$localize.lang()}

      <!-- Determining directionality, e.g. 'ltr' or 'rtl' -->
      ${this.$localize.dir()}


    *** HOW TO DEFINE TRANSLATIONS: ***
    // Simple terms
    upload: 'Upload',

    // Terms with placeholders
    greetUser: (name: string) => `Hello, ${name}!`,

    // Plurals
    numFilesSelected: (count: number) => {
        if (count === 0) return 'No files selected';
        if (count === 1) return '1 file selected';
        return `${count} files selected`;
    }
 */
const LocalizeMixin = (Base) => class extends Base {
    constructor() {
        super(...arguments);
        this.localize = new LocalizeController(this);
    }
    // Provide default values to avoid definite assignment errors and avoid decorators
    // commentati perchè danno problemi su React.js. Sono attributi nativi, e assegnare un valore di default a react da fastidio
    // dir: string = '';
    // lang: string = '';
    /**
     * Restituisce tutta l'utility di traduzione
     *

     *
     * @returns tutta l'utility di traduzione
     *
     * @example
     * this.$localize.lang() -> ritorna la lingua corrente
     * this.$localize.dir() -> ritorna la direzione della lingua corrente
     */
    get $localize() {
        return this.localize;
    }
    /**
     * Restituisce una stringa localizzata a partire da una chiave di termine.
     *
     * Utilizza il `LocalizeController` per accedere al dizionario corrente e
     * tradurre la chiave fornita secondo la lingua attiva.
     *
     * @param t - La chiave del termine da localizzare (es. 'hello', 'submit', ecc.).
     * @returns La stringa tradotta in base alla lingua attiva. Se la chiave non è trovata, restituisce la chiave stessa.
     *
     * @example
     * this.$t('hello'); // → "Ciao" (in locale it-IT)
     */
    $t(t) {
        // format term
        return this.localize.term(t);
    }
    /**
     * Formatta una data in base alla localizzazione attiva.
     *
     * Utilizza il `LocalizeController` per restituire una stringa localizzata
     * secondo le opzioni fornite (es. mese esteso, anno, ecc.).
     *
     * @param n - La data da formattare come stringa compatibile (es. ISO o con timezone, es. '2021-09-15 14:00:00 ET').
     * @param p - Le opzioni di formattazione per `Intl.DateTimeFormat` (es. { year: 'numeric', month: 'long', day: 'numeric' }).
     * @returns Una stringa rappresentante la data formattata secondo la localizzazione attiva.
     *
     * @example
     * this.$d('2021-09-15 14:00:00 ET', { year: 'numeric', month: 'long', day: 'numeric' });
     * // → "15 settembre 2021" (in locale it-IT)
     */
    $d(d, p) {
        // format date
        return this.localize.date(d, p);
    }
    /**
     * Formatta un numero secondo le impostazioni locali dell'utente corrente.
     *
     * Utilizza il `LocalizeController` per applicare formattazione numerica,
     * incluse opzioni come separatori, decimali, valute, ecc.
     *
     * @param d - Il numero da formattare.
     * @param p - Le opzioni di formattazione (es. { style: 'currency', currency: 'EUR' }).
     * @returns Una stringa rappresentante il numero formattato secondo la localizzazione attiva.
     *
     * @example
     * this.$n(1234.56, { style: 'currency', currency: 'USD' }); // → "$1,234.56" (in locale en-US)
     */
    $n(d, p) {
        return this.localize.number(d, p);
    }
};

/* eslint-disable no-console */
class Logger {
    constructor(tag) {
        this.tag = tag;
    }
    format(level, msg) {
        return [`[${this.tag}] ${msg}`];
    }
    warn(msg) {
        console.warn(...this.format('warn', msg));
    }
    error(msg) {
        console.error(...this.format('error', msg));
    }
    info(msg) {
        console.info(...this.format('info', msg));
    }
}

/**
 * Factory function per creare una base class estendibile
 * con stili personalizzati.
 */
class BaseComponent extends LitElement {
    constructor() {
        super();
        this.composeClass = clsx;
        this.logger = new Logger(this.tagName.toLowerCase());
    }
    get _ariaAttributes() {
        const attributes = {};
        for (const attr of this.getAttributeNames()) {
            if (attr === 'it-role') {
                attributes.role = this.getAttribute(attr);
            }
            else if (attr.startsWith('it-aria-')) {
                attributes[attr.replace(/^it-/, '')] = this.getAttribute(attr);
            }
        }
        return attributes;
    }
    // eslint-disable-next-line class-methods-use-this
    generateId(prefix) {
        return `${prefix}-${Math.random().toString(36).slice(2)}`;
    }
    // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
    addFocus(element) {
        // new TrackFocus(element); // per il momento è stato disattivato perchè ci sono le pseudo classi ::focus-visible per fare quello che fa TrackFocus. Si possono aggiungere regole css in bsi-italia 3 dato che stiamo facendo una breaking release di bsi.
    }
    // eslint-disable-next-line class-methods-use-this
    getActiveElement() {
        let active = document.activeElement;
        while (active && active.shadowRoot && active.shadowRoot.activeElement) {
            active = active.shadowRoot.activeElement;
        }
        return active;
    }
    get focusElement() {
        return this;
    }
    // eslint-disable-next-line class-methods-use-this
    get prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    connectedCallback() {
        super.connectedCallback();
        // generate internal _id
        const prefix = this.id?.length > 0 ? this.id : this.tagName.toLowerCase();
        this._id = this.generateId(prefix);
        this.ariaAttributesObserver = new MutationObserver((mutations) => {
            const shouldUpdate = mutations.some((mutation) => {
                if (mutation.type !== 'attributes' || !mutation.attributeName) {
                    return false;
                }
                return mutation.attributeName === 'it-role' || mutation.attributeName.startsWith('it-aria-');
            });
            if (shouldUpdate) {
                this.requestUpdate();
            }
        });
        this.ariaAttributesObserver.observe(this, {
            attributes: true,
            attributeOldValue: true,
        });
    }
    disconnectedCallback() {
        this.ariaAttributesObserver?.disconnect();
        this.ariaAttributesObserver = undefined;
        super.disconnectedCallback();
    }
}
const BaseLocalizedComponent = LocalizeMixin(BaseComponent);

//
// We store a WeakMap of forms + controls so we can keep references to all FormControl within a given form. As
// elements connect and disconnect to/from the DOM, their containing form is used as the key and the form control is
// added and removed from the form's set, respectively.
//
const formCollections = new WeakMap();
//
// We store a WeakMap of reportValidity() overloads so we can override it when form controls connect to the DOM and
// restore the original behavior when they disconnect.
//
const reportValidityOverloads = new WeakMap();
const checkValidityOverloads = new WeakMap();
//
// We store a Set of controls that users have interacted with. This allows us to determine the interaction state
// without littering the DOM with additional data attributes.
//
const userInteractedControls = new WeakSet();
//
// We store a WeakMap of interactions for each form control so we can track when all conditions are met for validation.
//
const interactions = new WeakMap();
/** A reactive controller to allow form controls to participate in form submission, validation, etc. */
class FormControlController {
    constructor(host, options) {
        this.submittedOnce = false;
        this.handleFormData = (event) => {
            // console.log('handleFormData');
            const disabled = this.options.disabled(this.host);
            const name = this.options.name(this.host);
            const value = this.options.value(this.host);
            const tagName = this.host.tagName.toLowerCase();
            // For buttons, we only submit the value if they were the submitter. This is currently done in doAction() by
            // injecting the name/value on a temporary button, so we can just skip them here.
            const isButton = tagName === 'it-button';
            if (this.host.isConnected &&
                !disabled &&
                !isButton &&
                typeof name === 'string' &&
                name.length > 0 &&
                typeof value !== 'undefined') {
                switch (tagName) {
                    case 'it-radio':
                        if (this.host.checked) {
                            event.formData.append(name, value);
                        }
                        break;
                    case 'it-checkbox':
                    case 'it-toggle':
                        if (this.host.checked) {
                            if (event.formData.getAll(name).indexOf(value) < 0) {
                                // handle group checkbox
                                event.formData.append(name, value);
                            }
                        }
                        break;
                    case 'it-checkbox-group':
                    case 'it-toggle-group':
                        // non settare valori in formData, perchè ogni singola checkbox setta il suo valore
                        break;
                    case 'it-upload':
                        // value is File[] — append each File object directly (not as string)
                        if (Array.isArray(value)) {
                            value.forEach((file) => {
                                event.formData.append(name, file);
                            });
                        }
                        break;
                    default:
                        if (Array.isArray(value)) {
                            value.forEach((val) => {
                                event.formData.append(name, val.toString());
                            });
                        }
                        else {
                            event.formData.append(name, value.toString());
                        }
                }
            }
        };
        this.handleFormSubmit = (event) => {
            const disabled = this.options.disabled(this.host);
            const reportValidity = this.options.reportValidity;
            // Update the interacted state for all controls when the form is submitted
            if (this.form && !this.form.noValidate) {
                formCollections.get(this.form)?.forEach((control) => {
                    this.setUserInteracted(control, true);
                });
            }
            const resReportValidity = reportValidity(this.host);
            if (this.form && !this.form.noValidate && !disabled && !resReportValidity) {
                event.preventDefault();
                // event.stopImmediatePropagation(); // se lo attiviamo, valida un campo alla volta
                // Scroll al primo controllo non valido
                const formControls = formCollections.get(this.form);
                if (formControls) {
                    for (const control of formControls) {
                        if (!control.validity?.valid) {
                            // Scroll smooth verso il controllo non valido
                            control.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                            });
                            break;
                        }
                    }
                }
            }
            this.submittedOnce = true;
        };
        this.handleFormReset = () => {
            this.options.setValue(this.host, '');
            this.submittedOnce = false;
            this.setUserInteracted(this.host, false);
            interactions.set(this.host, []);
        };
        this.handleInteraction = (event) => {
            const emittedEvents = interactions.get(this.host);
            if (!emittedEvents.includes(event.type)) {
                emittedEvents.push(event.type);
            }
            // Mark it as user-interacted as soon as all associated events have been emitted
            if (emittedEvents.length === this.options.assumeInteractionOn.length) {
                this.setUserInteracted(this.host, true);
            }
        };
        this.checkFormValidity = () => {
            // console.log('checkFormValidity');
            //
            // This is very similar to the `reportFormValidity` function, but it does not trigger native constraint validation
            // Allow the user to simply check if the form is valid and handling validity in their own way.
            //
            // We preserve the original method in a WeakMap, but we don't call it from the overload because that would trigger
            // validations in an unexpected order. When the element disconnects, we revert to the original behavior. This won't
            // be necessary once we can use ElementInternals.
            //
            // Note that we're also honoring the form's novalidate attribute.
            //
            if (this.form && !this.form.noValidate) {
                // This seems sloppy, but checking all elements will cover native inputs, Shoelace inputs, and other custom
                // elements that support the constraint validation API.
                const elements = this.form.querySelectorAll('*');
                for (const element of elements) {
                    if (typeof element.checkValidity === 'function') {
                        if (!element.checkValidity()) {
                            return false;
                        }
                    }
                }
            }
            return true;
        };
        this.reportFormValidity = () => {
            // console.log('reportFormValidity');
            //
            // FormControl work hard to act like regular form controls. They support the Constraint Validation API
            // and its associated methods such as setCustomValidity() and reportValidity(). However, the HTMLFormElement also
            // has a reportValidity() method that will trigger validation on all child controls. Since we're not yet using
            // ElementInternals, we need to overload this method so it looks for any element with the reportValidity() method.
            //
            // We preserve the original method in a WeakMap, but we don't call it from the overload because that would trigger
            // validations in an unexpected order. When the element disconnects, we revert to the original behavior. This won't
            // be necessary once we can use ElementInternals.
            //
            // Note that we're also honoring the form's novalidate attribute.
            //
            if (this.form && !this.form.noValidate) {
                // This seems sloppy, but checking all elements will cover native inputs, Shoelace inputs, and other custom
                // elements that support the constraint validation API.
                const elements = this.form.querySelectorAll('*');
                for (const element of elements) {
                    if (typeof element.reportValidity === 'function') {
                        if (!element.reportValidity()) {
                            return false;
                        }
                    }
                }
            }
            return true;
        };
        (this.host = host).addController(this);
        this.options = {
            form: (input) => {
                // If there's a form attribute, use it to find the target form by id
                // Controls may not always reflect the 'form' property. For example, `<it-button>` doesn't reflect.
                const formId = input.form;
                if (formId) {
                    const root = input.getRootNode();
                    const form = root.querySelector(`#${formId}`);
                    if (form) {
                        return form;
                    }
                }
                return input.closest('form');
            },
            name: (input) => input.name,
            value: (input) => input.value,
            disabled: (input) => input.disabled ?? false,
            reportValidity: (input) => typeof input.reportValidity === 'function' ? input.reportValidity() : true,
            checkValidity: (input) => (typeof input.checkValidity === 'function' ? input.checkValidity() : true),
            setValue: (input, value) => {
                // eslint-disable-next-line no-param-reassign
                input.value = value;
            },
            assumeInteractionOn: ['it-input'],
            ...options,
        };
    }
    hostConnected() {
        const form = this.options.form(this.host);
        if (form) {
            this.attachForm(form);
        }
        // Listen for interactions
        interactions.set(this.host, []);
        this.options.assumeInteractionOn.forEach((event) => {
            this.host.addEventListener(event, this.handleInteraction);
        });
    }
    hostDisconnected() {
        this.detachForm();
        // Clean up interactions
        interactions.delete(this.host);
        this.options.assumeInteractionOn.forEach((event) => {
            this.host.removeEventListener(event, this.handleInteraction);
        });
    }
    hostUpdated() {
        const form = this.options.form(this.host);
        // Detach if the form no longer exists
        if (!form) {
            this.detachForm();
        }
        // If the form has changed, reattach it
        if (form && this.form !== form) {
            this.detachForm();
            this.attachForm(form);
        }
        if (this.host.hasUpdated) {
            this.setValidity(this.host.validity.valid);
        }
    }
    attachForm(form) {
        if (form) {
            this.form = form;
            // Add this element to the form's collection
            if (formCollections.has(this.form)) {
                formCollections.get(this.form).add(this.host);
            }
            else {
                formCollections.set(this.form, new Set([this.host]));
            }
            this.form.addEventListener('formdata', this.handleFormData);
            this.form.addEventListener('submit', this.handleFormSubmit);
            this.form.addEventListener('reset', this.handleFormReset);
            // Overload the form's reportValidity() method so it looks at FormControl
            if (!reportValidityOverloads.has(this.form)) {
                reportValidityOverloads.set(this.form, this.form.reportValidity);
                this.form.reportValidity = () => this.reportFormValidity();
            }
            // Overload the form's checkValidity() method so it looks at FormControl
            if (!checkValidityOverloads.has(this.form)) {
                checkValidityOverloads.set(this.form, this.form.checkValidity);
                this.form.checkValidity = () => this.checkFormValidity();
            }
        }
        else {
            this.form = undefined;
        }
    }
    detachForm() {
        if (!this.form)
            return;
        const formCollection = formCollections.get(this.form);
        if (!formCollection) {
            return;
        }
        this.submittedOnce = false;
        // Remove this host from the form's collection
        formCollection.delete(this.host);
        // Check to make sure there's no other form controls in the collection. If we do this
        // without checking if any other controls are still in the collection, then we will wipe out the
        // validity checks for all other elements.
        // see: https://github.com/shoelace-style/shoelace/issues/1703
        if (formCollection.size <= 0) {
            this.form.removeEventListener('formdata', this.handleFormData);
            this.form.removeEventListener('submit', this.handleFormSubmit);
            this.form.removeEventListener('reset', this.handleFormReset);
            // Remove the overload and restore the original method
            if (reportValidityOverloads.has(this.form)) {
                this.form.reportValidity = reportValidityOverloads.get(this.form);
                reportValidityOverloads.delete(this.form);
            }
            if (checkValidityOverloads.has(this.form)) {
                this.form.checkValidity = checkValidityOverloads.get(this.form);
                checkValidityOverloads.delete(this.form);
            }
            // So it looks weird here to not always set the form to undefined. But I _think_ if we unattach this.form here,
            // we end up in this fun spot where future validity checks don't have a reference to the form validity handler.
            // First form element in sets the validity handler. So we can't clean up `this.form` until there are no other form elements in the form.
            this.form = undefined;
        }
    }
    // eslint-disable-next-line class-methods-use-this
    setUserInteracted(el, hasInteracted) {
        if (hasInteracted) {
            userInteractedControls.add(el);
        }
        else {
            userInteractedControls.delete(el);
        }
        el.requestUpdate();
    }
    doAction(type, submitter) {
        // console.log('doaction', type);
        if (this.form) {
            const button = document.createElement('button');
            button.type = type;
            button.style.position = 'absolute';
            button.style.width = '0';
            button.style.height = '0';
            button.style.clipPath = 'inset(50%)';
            button.style.overflow = 'hidden';
            button.style.whiteSpace = 'nowrap';
            // Pass name, value, and form attributes through to the temporary button
            if (submitter) {
                button.name = submitter.name;
                button.value = submitter.value;
                ['formaction', 'formenctype', 'formmethod', 'formnovalidate', 'formtarget'].forEach((attr) => {
                    if (submitter.hasAttribute(attr)) {
                        button.setAttribute(attr, submitter.getAttribute(attr));
                    }
                });
            }
            this.form.append(button);
            button.click();
            button.remove();
        }
    }
    /** Returns the associated `<form>` element, if one exists. */
    getForm() {
        return this.form ?? null;
    }
    /** Resets the form, restoring all the control to their default value */
    reset(submitter) {
        this.doAction('reset', submitter);
    }
    /** Submits the form, triggering validation and form data injection. */
    submit(submitter) {
        // Calling form.submit() bypasses the submit event and constraint validation. To prevent this, we can inject a
        // native submit button into the form, "click" it, then remove it to simulate a standard form submission.
        this.doAction('submit', submitter);
    }
    /**
     * Synchronously sets the form control's validity. Call this when you know the future validity but need to update
     * the host element immediately, i.e. before Lit updates the component in the next update.
     */
    setValidity(isValid) {
        const host = this.host;
        const hasInteracted = Boolean(userInteractedControls.has(host));
        const required = Boolean(host.required);
        //
        // We're mapping the following "states" to data attributes. In the future, we can use ElementInternals.states to
        // create a similar mapping, but instead of [data-invalid] it will look like :--invalid.
        //
        // See this RFC for more details: https://github.com/shoelace-style/shoelace/issues/1011
        //
        host.toggleAttribute('data-required', required);
        host.toggleAttribute('data-optional', !required);
        host.toggleAttribute('data-invalid', !isValid);
        host.toggleAttribute('data-valid', isValid);
        host.toggleAttribute('data-user-invalid', !isValid && hasInteracted);
        host.toggleAttribute('data-user-valid', isValid && hasInteracted);
    }
    userInteracted() {
        const hasInteracted = Boolean(userInteractedControls.has(this.host));
        return hasInteracted;
    }
    /**
     * Updates the form control's validity based on the current value of `host.validity.valid`. Call this when anything
     * that affects constraint validation changes so the component receives the correct validity states.
     */
    updateValidity() {
        const host = this.host;
        this.setValidity(host.validity.valid);
    }
    /**
     * Dispatches a non-bubbling, cancelable custom event of type `it-invalid`.
     * If the `it-invalid` event will be cancelled then the original `invalid`
     * event (which may have been passed as argument) will also be cancelled.
     * If no original `invalid` event has been passed then the `it-invalid`
     * event will be cancelled before being dispatched.
     */
    emitInvalidEvent(originalInvalidEvent) {
        const itInvalidEvent = new CustomEvent('it-invalid', {
            bubbles: false,
            composed: false,
            cancelable: true,
            detail: {},
        });
        if (!originalInvalidEvent) {
            itInvalidEvent.preventDefault();
        }
        if (!this.host.dispatchEvent(itInvalidEvent)) {
            originalInvalidEvent?.preventDefault();
        }
    }
}

const translation$1 = {
    $code: 'it',
    $name: 'Italiano',
    $dir: 'ltr',
    validityRequired: 'Questo campo è obbligatorio.',
    validityGroupRequired: "Scegli almeno un'opzione",
    validityPattern: 'Il valore non corrisponde al formato richiesto.',
    validityMinlength: 'Il valore deve essere lungo almeno {minlength} caratteri.',
    validityMaxlength: 'Il valore deve essere lungo al massimo {maxlength} caratteri.',
};

registerTranslation(translation$1);
class FormControl extends BaseLocalizedComponent {
    constructor() {
        super(...arguments);
        this.formControlController = new FormControlController(this, {
            assumeInteractionOn: ['it-input', 'it-blur', 'it-change'],
        });
        // TODO: verificare se serve davvero con il fatto che usiamo form-controller
        // static formAssociated = true;
        // @property()
        // internals = this.attachInternals();
        this._touched = false;
        /** The name of the input, submitted as a name/value pair with form data. */
        this.name = '';
        /** The current value of the input, submitted as a name/value pair with form data. */
        this.value = '';
        /** If the input is disabled. */
        this.disabled = false;
        /**
         * By default, form controls are associated with the nearest containing `<form>` element. This attribute allows you
         * to place the form control outside of a form and associate it with the form that has this `id`. The form must be in
         * the same document or shadow root for this to work.
         */
        this.form = '';
        /** If you implement your custom validation and you won't to trigger default validation */
        this.customValidation = false;
        /** If your input is invalid from your custom validition, set this attribute with message validation */
        this.validationText = '';
        /**
         * Specifies the granularity that the value must adhere to, or the special value `any` which means no stepping is
         * implied, allowing any numeric value. Only applies to date and number input types.
         */
        this.step = 'any';
        /** The input's minimum length. */
        this.minlength = -1;
        /** The input's maximum length. */
        this.maxlength = -1;
        /** If the input is required. */
        this.required = false;
        /* For grouped input, like checkbox-group */
        this.isInGroup = false;
        this.validationMessage = '';
    }
    /** Gets the validity state object */
    get validity() {
        return this.inputElement?.validity;
    }
    /** Gets the associated form, if one exists. */
    getForm() {
        return this.formControlController.getForm();
    }
    // Form validation methods
    checkValidity() {
        const inputValid = this.inputElement?.checkValidity() ?? true; // this.inputElement.checkValidity() è la validazione del browser
        return inputValid;
    }
    /** Checks for validity and shows the browser's validation message if the control is invalid. */
    reportValidity() {
        // const ret = this.inputElement.checkValidity();
        const ret = this.checkValidity(); // chiama la checkValidity, che se è stata overridata chiama quella
        this.handleValidationMessages();
        return ret; // this.inputElement.reportValidity();
    }
    /** Sets a custom validation message. Pass an empty string to restore validity. */
    setCustomValidity(message) {
        this.inputElement.setCustomValidity(message);
        this.validationMessage = this.inputElement.validationMessage;
        this.formControlController.updateValidity();
    }
    // Handlers
    _handleReady() {
        requestAnimationFrame(() => {
            this.dispatchEvent(new CustomEvent('it-input-ready', { bubbles: true, detail: { el: this.inputElement } }));
        });
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleInput(e) {
        this.handleValidationMessages();
        this.dispatchEvent(new CustomEvent('it-input', {
            detail: { value: this.inputElement.value, el: this.inputElement },
            bubbles: true,
            composed: true,
        }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleBlur(e) {
        this._touched = true;
        this.handleValidationMessages();
        this.dispatchEvent(new FocusEvent('it-blur', { bubbles: true, composed: true }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleFocus(e) {
        this.dispatchEvent(new FocusEvent('it-focus', { bubbles: true, composed: true }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleClick(e) {
        this.dispatchEvent(new MouseEvent('it-click', { bubbles: true, composed: true }));
    }
    /*
    Override default browser validation messages
     */
    handleValidationMessages() {
        if (!this.customValidation) {
            const _v = this.inputElement.validity;
            const isRequiredHandledByGroup = this.isInGroup === true;
            if (_v.valueMissing && !isRequiredHandledByGroup) {
                this.setCustomValidity(this.$t('validityRequired'));
            }
            else if (_v.patternMismatch) {
                this.setCustomValidity(this.$t('validityPattern'));
            }
            else if (_v.tooShort) {
                this.setCustomValidity(this.$t('validityMinlength').replace('{minlength}', this.minlength.toString()));
            }
            else if (_v.tooLong) {
                this.setCustomValidity(this.$t('validityMaxlength').replace('{maxlength}', this.maxlength.toString()));
            }
            else {
                /* nothing. Usa il messaggio di errore della validazione
                di default del browser per altri errori di validità come:
                - typeMismatch
                - rangeUnderflow
                - rangeOverflow
                - stepMismatch
                - badInput */
                const otherConstraintErrors = _v.typeMismatch || _v.rangeUnderflow || _v.rangeOverflow || _v.stepMismatch || _v.badInput;
                if (!otherConstraintErrors) {
                    this.setCustomValidity('');
                }
            }
        }
        this.validationMessage = this.inputElement.validationMessage;
    }
    _handleInvalid(event) {
        this.formControlController.setValidity(false);
        this.formControlController.emitInvalidEvent(event);
    }
    _handleChange(e) {
        const target = e.target;
        let value;
        if (target instanceof HTMLInputElement) {
            switch (target.type) {
                case 'checkbox':
                case 'radio':
                    value = target.checked;
                    break;
                case 'file':
                    value = target.files; // FileList
                    break;
                default:
                    value = target.value;
            }
        }
        else if (target instanceof HTMLSelectElement) {
            if (target.multiple) {
                value = Array.from(target.selectedOptions).map((o) => o.value);
            }
            else {
                value = target.value;
            }
        }
        else {
            // textarea o altri input con value
            value = target.value;
        }
        this.dispatchEvent(new CustomEvent('it-change', {
            detail: { value, el: target },
            bubbles: true,
            composed: true,
        }));
    }
    updated(changedProperties) {
        super.updated?.(changedProperties);
        if (this.customValidation) {
            this.setCustomValidity(this.validationText ?? '');
        }
        else if (this.formControlController.userInteracted()) {
            this.formControlController.updateValidity();
        }
    }
}
__decorate([
    state(),
    __metadata("design:type", Object)
], FormControl.prototype, "_touched", void 0);
__decorate([
    query('.it-form__control'),
    __metadata("design:type", HTMLInputElement)
], FormControl.prototype, "inputElement", void 0);
__decorate([
    property({ type: String, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "name", void 0);
__decorate([
    property({ reflect: true }),
    __metadata("design:type", Object)
], FormControl.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "disabled", void 0);
__decorate([
    property({ reflect: true, type: String }),
    __metadata("design:type", Object)
], FormControl.prototype, "form", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'custom-validation' }),
    __metadata("design:type", Object)
], FormControl.prototype, "customValidation", void 0);
__decorate([
    property({ attribute: 'validity-message', reflect: true }),
    __metadata("design:type", String)
], FormControl.prototype, "validationText", void 0);
__decorate([
    property(),
    __metadata("design:type", String)
], FormControl.prototype, "pattern", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "min", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "max", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "step", void 0);
__decorate([
    property({ type: Number }),
    __metadata("design:type", Object)
], FormControl.prototype, "minlength", void 0);
__decorate([
    property({ type: Number }),
    __metadata("design:type", Object)
], FormControl.prototype, "maxlength", void 0);
__decorate([
    property({ type: Boolean, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "required", void 0);
__decorate([
    property({ type: Boolean }),
    __metadata("design:type", Object)
], FormControl.prototype, "isInGroup", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], FormControl.prototype, "validationMessage", void 0);

/**
 * Dispatches a cancelable custom event and runs the provided default action
 * only if the consumer did not call `event.preventDefault()`.
 *
 * This is the shared convention used across components (Tabs, Modal, Chip, Alert,
 * Forward, Back) to provide a sensible default JavaScript behavior out of the box
 * while letting consumers intercept and override it. Notification deliberately does
 * NOT use this: its show/close are always caller-initiated (no default markup-driven
 * trigger to override), so there was no real use case for blocking them — see
 * it-notification.ts for the plain (non-cancelable) events it fires instead.
 *
 * ```js
 * el.addEventListener('it-alert-close', (e) => {
 *   e.preventDefault();                    // block the default removal
 *   fetchConfirmation().then((ok) => {     // async logic runs after prevention
 *     if (ok) e.target.close();
 *   });
 * });
 * ```
 *
 * Note: `preventDefault()` must be called synchronously inside the listener.
 * Async code can run afterward; call the component's public method when ready.
 *
 * @param host the element dispatching the event
 * @param name the event name (kebab-case, `it-` prefixed)
 * @param detail the event `detail` payload
 * @param defaultAction the behavior executed when the event is not prevented
 * @returns the dispatched event (inspect `defaultPrevented` if needed)
 */
function dispatchCancelable(host, name, detail, defaultAction) {
    const event = new CustomEvent(name, {
        bubbles: true,
        composed: true,
        cancelable: true,
        detail,
    });
    host.dispatchEvent(event);
    if (!event.defaultPrevented) {
        defaultAction();
    }
    return event;
}

if (typeof window !== 'undefined') {
    window._itAnalytics = window._itAnalytics || {};
    window._itAnalytics = {
        ...window._itAnalytics,
        version: '1.0.0-alpha.19',
    };
}

const translation = {
    $code: 'it',
    $name: 'Italiano',
    $dir: 'ltr',
    transferSourceLabel: 'Sorgente',
    transferTargetLabel: 'Destinazione',
    transferLabel: 'Trasferisci selezionati a destra',
    transferBackLabel: 'Trasferisci selezionati a sinistra',
    transferResetLabel: 'Ripristina stato iniziale',
    transferItemsCount: '{count} elementi',
    transferSelectAll: 'Seleziona tutto',
};

var styles = css`*,
*::before,
*::after {
  box-sizing: border-box;
}

@media (prefers-reduced-motion: no-preference) {
  :root {
    scroll-behavior: smooth;
  }
}

body {
  margin: 0;
  -webkit-text-size-adjust: 100%;
  -webkit-tap-highlight-color: hsla(0, 0%, 0%, 0);
}

hr {
  margin: 1rem 0;
  color: inherit;
  border: 0;
  border-top: 1px solid;
  opacity: 0.25;
}

p {
  margin-top: 0;
  margin-bottom: var(--bsi-paragraph-spacing);
}

abbr[title] {
  text-decoration: underline dotted;
  cursor: help;
  text-decoration-skip-ink: none;
}

address {
  margin-bottom: var(--bsi-spacing-s);
  font-style: normal;
  line-height: inherit;
}

ol,
ul {
  padding-left: var(--bsi-spacing-l);
}

ol,
ul,
dl {
  margin-top: 0;
  margin-bottom: var(--bsi-spacing-s);
}

ol ol,
ul ul,
ol ul,
ul ol {
  margin-bottom: 0;
}

dt {
  font-weight: var(--bsi-font-weight-strong);
}

dd {
  margin-bottom: var(--bsi-spacing-xxs);
  margin-left: 0;
}

blockquote {
  margin: 0 0 var(--bsi-spacing-s);
}

sub,
sup {
  position: relative;
  font-size: var(--bsi-font-size-1);
  line-height: 0;
  vertical-align: baseline;
}

sub {
  bottom: -0.25em;
}

sup {
  top: -0.5em;
}

a {
  color: var(--bsi-color-link);
  text-decoration: underline;
  text-decoration-skip-ink: auto;
  text-underline-offset: 2px;
}
a:hover {
  color: var(--bsi-color-link-hover);
  cursor: pointer;
}

pre,
code,
kbd,
samp {
  font-size: 1em;
}

pre {
  display: block;
  margin-top: 0;
  margin-bottom: var(--bsi-paragraph-spacing);
  overflow: auto;
}
pre code {
  word-break: normal;
}

a > code {
  color: inherit;
}

figure {
  margin: 0 0 1rem;
}

img,
svg {
  vertical-align: middle;
}

table {
  caption-side: bottom;
  border-collapse: collapse;
}

caption {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  color: hsl(210, 17%, 44%);
  text-align: left;
}

th {
  text-align: inherit;
  text-align: -webkit-match-parent;
}

thead,
tbody,
tfoot,
tr,
td,
th {
  border-color: inherit;
  border-style: solid;
  border-width: 0;
}

label {
  display: inline-block;
}

button {
  border-radius: 0;
}

button:focus:not(:focus-visible) {
  outline: 0;
}

input,
button,
select,
optgroup,
textarea {
  margin: 0;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

button,
select {
  text-transform: none;
}

[role=button] {
  cursor: pointer;
}

select {
  word-wrap: normal;
}
select:disabled {
  opacity: 1;
}

[list]:not([type=date]):not([type=datetime-local]):not([type=month]):not([type=week]):not([type=time])::-webkit-calendar-picker-indicator {
  display: none !important;
}

button,
[type=button],
[type=reset],
[type=submit] {
  -webkit-appearance: button;
}
button:not(:disabled),
[type=button]:not(:disabled),
[type=reset]:not(:disabled),
[type=submit]:not(:disabled) {
  cursor: pointer;
}

::-moz-focus-inner {
  padding: 0;
  border-style: none;
}

textarea {
  resize: vertical;
}

fieldset {
  min-width: 0;
  padding: 0;
  margin: 0;
  border: 0;
}

::-webkit-datetime-edit-fields-wrapper,
::-webkit-datetime-edit-text,
::-webkit-datetime-edit-minute,
::-webkit-datetime-edit-hour-field,
::-webkit-datetime-edit-day-field,
::-webkit-datetime-edit-month-field,
::-webkit-datetime-edit-year-field {
  padding: 0;
}

::-webkit-inner-spin-button {
  height: auto;
}

[type=search] {
  outline-offset: -2px;
  -webkit-appearance: textfield;
}

/* rtl:raw:
[type="tel"],
[type="url"],
[type="email"],
[type="number"] {
  direction: ltr;
}
*/
::-webkit-search-decoration {
  -webkit-appearance: none;
}

::-webkit-color-swatch-wrapper {
  padding: 0;
}

::file-selector-button {
  font: inherit;
  -webkit-appearance: button;
}

output {
  display: inline-block;
}

iframe {
  border: 0;
}

summary {
  display: list-item;
  cursor: pointer;
}

progress {
  vertical-align: baseline;
}

[hidden] {
  display: none !important;
}

.visually-hidden,
.visually-hidden-focusable:not(:focus):not(:focus-within) {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

:root {
  --bsi-form-control-height: 2.5rem;
  --bsi-form-control-spacing: var(--bsi-spacing-xxs);
  --bsi-form-control-background-color: var(--bsi-color-background-inverse);
  --bsi-form-control-border-color: var(--bsi-color-border-secondary);
  --bsi-form-control-border-radius: var(--bsi-radius-smooth);
  --bsi-form-control-placeholder-color: var(--bsi-color-text-muted);
  --bsi-form-control-label-color: var(--bsi-color-text-base);
  --bsi-form-control-text-color: var(--bsi-color-text-secondary);
  --bsi-form-control-font-size: var(--bsi-body-font-size);
  --bsi-form-group-spacing-y: var(--bsi-spacing-m);
  --bsi-form-checkbox-border-color: var(--bsi-color-border-secondary);
  --bsi-form-checkbox-border-radius: var(--bsi-radius-smooth);
  --bsi-form-checked-color: var(--bsi-color-background-primary);
}

input[readonly],
textarea[readonly],
select[readonly] {
  --bsi-form-control-border-color: var(--bsi-color-border-subtle);
  --bsi-form-control-background-color: var(--bsi-color-background-muted);
  cursor: not-allowed;
}

input,
textarea,
select {
  display: block;
  width: 100%;
  padding: var(--bsi-form-control-spacing);
  border: 1px solid var(--bsi-form-control-border-color);
  border-radius: var(--bsi-form-control-border-radius);
  background-color: var(--bsi-form-control-background-color);
  color: var(--bsi-form-control-text-color);
  font-size: var(--bsi-form-control-font-size);
}
input.disabled, input:disabled,
textarea.disabled,
textarea:disabled,
select.disabled,
select:disabled {
  border-color: var(--bsi-color-border-disabled);
  opacity: 1;
  background-color: var(--bsi-color-background-disabled);
  color: var(--bsi-color-text-disabled);
}

/* stylelint-disable-next-line no-duplicate-selectors */
input:focus,
textarea:focus {
  outline: 3px solid transparent;
  outline-offset: 3px;
  box-shadow: 0 0 0 2px var(--bsi-color-border-inverse), 0 0 0 5px var(--bsi-color-outline-focus) !important;
}

input::file-selector-button {
  margin: -0.375rem -0.75rem;
  padding: 0.375rem 0.75rem;
  border-width: 0;
  border-style: solid;
  border-radius: 0;
  border-color: inherit;
  color: hsl(0, 0%, 10%);
  pointer-events: none;
  margin-inline-end: 0.75rem;
  border-inline-end-width: 0;
  background-color: hsl(0, 0%, 100%);
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}
@media (prefers-reduced-motion: reduce) {
  input::file-selector-button {
    transition: none;
  }
}
input:hover:not(:disabled):not([readonly])::file-selector-button {
  background-color: rgb(242.25, 242.25, 242.25);
}
input[type=file] {
  overflow: hidden;
}
input[type=file]:not(:disabled):not([readonly]) {
  cursor: pointer;
}
input::-webkit-date-and-time-value {
  height: 1.5em;
}

select {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='hsl%280, 0%, 32%%29' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right var(--bsi-form-control-spacing) center;
  background-size: 16px 12px;
  appearance: none;
}
select:focus {
  border-color: hsl(210, 17%, 44%);
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(0, 102, 204, 0.25);
}
select[multiple], select[size]:not([size="1"]) {
  padding-right: 0.75rem;
}
select:disabled {
  background-color: hsl(0, 0%, 90%);
}
select:disabled:hover {
  cursor: not-allowed;
}
select:-moz-focusring {
  color: transparent;
  text-shadow: 0 0 0 hsl(0, 0%, 10%);
}
select option {
  font-weight: normal;
}
select {
  /* stylelint-disable-next-line no-duplicate-selectors */ /* TO DO check these styles */
}
select:disabled {
  opacity: 1;
  background-color: hsl(210, 3%, 85%);
}

textarea {
  height: auto;
  font-size: var(--bsi-body-font-size);
  line-height: 1.5;
}

label {
  display: inline-block;
  width: auto;
  max-width: 100%;
  margin-bottom: var(--bsi-spacing-xxs);
  color: var(--bsi-form-control-label-color);
  font-size: var(--bsi-label-font-size-s);
  font-weight: var(--bsi-font-weight-solid);
  line-height: var(--bsi-label-leading);
}

/* stylelint-disable-next-line no-duplicate-selectors */
input,
textarea {
  outline: 0;
  box-shadow: none;
  transition: none;
  appearance: none;
}

input[type=date],
input[type=datetime-local],
input[type=time] {
  display: flex;
}

fieldset legend {
  margin-bottom: var(--bsi-spacing-s);
  padding: 0 var(--bsi-form-input-spacing-x);
  background-color: transparent;
  color: var(--bsi-form-control-text-color);
  font-size: var(--bsi-label-font-size-s);
  font-weight: var(--bsi-font-weight-solid);
}

::placeholder {
  color: var(--bsi-form-control-placeholder-color);
}

input::-webkit-datetime-edit {
  background-color: var(--bsi-color-background-primary-lighter);
  color: var(--bsi-form-contro-text-color);
}

.form-group {
  position: relative;
  margin-bottom: var(--bsi-form-group-spacing-y);
}
.form-group label.input-symbol-label:not(.active) {
  left: 2.25rem;
}
.form-group small.form-text {
  margin: 0;
  font-size: 0.875rem;
}
.form-group input[type=time] ~ label {
  font-size: 0.875rem;
}

.form-text {
  margin: var(--bsi-spacing-xxs) 0;
  color: var(--bsi-color-text-secondary);
}

.form-group.active .form-file-name {
  padding-bottom: 1.95rem;
}

.warning-feedback {
  display: none;
  width: 100%;
  margin-top: 0.25rem;
  color: var(--bsi-color-text-warning);
  font-size: 0.75rem;
}

.valid-feedback,
.invalid-feedback,
.warning-feedback {
  margin-left: 0.5rem;
}

.input-group .input-group-text .btn {
  border-radius: var(--bsi-form-control-border-radius) 0 0 var(--bsi-form-control-border-radius);
}
.input-group .input-group-append {
  margin-left: 0;
}
.input-group .input-group-append .btn {
  height: 100%;
  padding-top: 0;
  padding-bottom: 0;
  border-bottom: 1px solid hsl(210, 17%, 44%);
  border-radius: 0 var(--bsi-form-control-border-radius) var(--bsi-form-control-border-radius) 0;
}

.form-check {
  position: relative;
}
.form-check + .form-check {
  margin-top: var(--bsi-spacing-s);
}
.form-check [type=checkbox],
.form-check [type=radio] {
  position: absolute;
  top: 9px;
  left: 9px;
  width: auto;
  margin-top: 0;
  margin-left: 0;
  opacity: 0;
}
.form-check [type=checkbox] + label,
.form-check [type=radio] + label {
  position: relative;
  margin-bottom: 0;
  padding-left: 28px;
  font-size: var(--bsi-label-font-size);
  font-weight: var(--bsi-font-weight-solid);
  cursor: pointer;
  user-select: none;
}
@media (min-width: 576px) {
  .form-check [type=checkbox] + label,
  .form-check [type=radio] + label {
    font-size: var(--bsi-label-font-size-l);
  }
}
.form-check [type=checkbox]:focus + label,
.form-check [type=radio]:focus + label {
  border-color: hsl(0, 0%, 0%) !important;
  box-shadow: 0 0 0 2px var(--bsi-color-border-inverse), 0 0 0 5px var(--bsi-color-outline-focus) !important;
  outline: 3px solid transparent !important;
  outline-offset: 3px !important;
}
.form-check [type=checkbox]:focus[data-focus-mouse=true] + label,
.form-check [type=radio]:focus[data-focus-mouse=true] + label {
  border-color: inherit !important;
  box-shadow: none !important;
  outline: none !important;
}
.form-check input[type=checkbox] + label::after,
.form-check input[type=checkbox] + label::before {
  position: absolute;
  left: 0;
  z-index: 1;
  content: "";
  border-width: 2px;
  border-style: solid;
  transition: all var(--bsi-transition-instant) ease-out;
}
.form-check input[type=checkbox] + label::after {
  top: 0;
  width: 20px;
  height: 20px;
  border-radius: var(--bsi-form-control-border-radius);
}
.form-check input[type=checkbox]:checked + label::before {
  top: 3px;
  left: 3px;
  width: 6px;
  height: 12px;
  border-width: 2px;
  border-style: solid;
  border-color: transparent var(--bsi-color-border-inverse) var(--bsi-color-border-inverse) transparent;
  opacity: 1;
  transform: rotate(40deg);
  transform-origin: 100% 100%;
  backface-visibility: hidden;
}
.form-check input[type=checkbox]:checked + label::after {
  z-index: 0;
  border-color: var(--bsi-form-checked-color);
  background-color: var(--bsi-form-checked-color);
}
.form-check input[type=checkbox]:not(:checked) + label::after {
  z-index: 0;
  border-color: var(--bsi-form-checkbox-border-color);
  background-color: transparent;
}
.form-check input[type=checkbox]:not(:checked) + label::before {
  top: 10px;
  left: 6px;
  width: 0;
  height: 0;
  border-color: transparent;
}
.form-check input[type=checkbox]:disabled + label {
  opacity: 1;
  cursor: not-allowed;
}
.form-check input[type=checkbox]:disabled:not(:checked) + label::after {
  border-color: var(--bsi-color-border-disabled);
  background-color: transparent;
}
.form-check input[type=checkbox]:disabled:checked + label::after {
  border-color: var(--bsi-color-border-disabled);
  background-color: var(--bsi-color-border-disabled);
}
.form-check input[type=radio] + label::after, .form-check input[type=radio] + label::before {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  content: "";
  width: 20px;
  height: 20px;
  border-width: 2px;
  border-style: solid;
  border-radius: var(--bsi-radius-rounded);
  transition: all var(--bsi-transition-instant) ease-out;
}
.form-check input[type=radio]:not(:checked) + label::after, .form-check input[type=radio]:not(:checked) + label::before {
  border-color: var(--bsi-form-checkbox-border-color);
}
.form-check input[type=radio]:not(:checked) + label:after {
  z-index: -1;
  transform: scale(0);
}
.form-check input[type=radio]:checked + label::after {
  z-index: 0;
  border-color: var(--bsi-form-checked-color);
  background-color: var(--bsi-form-checked-color);
  transform: scale(0.64);
}
.form-check input[type=radio]:checked + label::before {
  border-color: var(--bsi-form-checked-color);
}
.form-check input[type=radio]:disabled + label {
  cursor: not-allowed;
}
.form-check input[type=radio]:disabled:not(:checked) + label::after, .form-check input[type=radio]:disabled:not(:checked) + label::before {
  border-color: var(--bsi-color-border-disabled);
}
.form-check input[type=radio]:disabled:checked + label::after {
  border-color: var(--bsi-color-border-disabled);
  background-color: var(--bsi-color-border-disabled);
}
.form-check input[type=radio]:disabled:checked + label::before {
  border-color: var(--bsi-color-border-disabled);
}
.form-check .form-text {
  display: block;
  margin-bottom: 0.5rem;
  padding-right: 3.25rem;
}
.form-check.form-check-group {
  margin-bottom: 1rem;
  padding: 0 0 1rem 0;
  box-shadow: inset 0 -1px 0 0 rgba(1, 1, 1, 0.1);
}
.form-check.form-check-group input[type=checkbox] + label,
.form-check.form-check-group input[type=radio] + label {
  position: static;
  padding-right: 3.25rem;
  padding-left: 0;
}
.form-check.form-check-group input[type=checkbox] + label::after, .form-check.form-check-group input[type=checkbox] + label::before,
.form-check.form-check-group input[type=radio] + label::after,
.form-check.form-check-group input[type=radio] + label::before {
  right: 0;
  left: auto;
}
.form-check.form-check-group input[type=checkbox]:checked + label::before {
  right: 11px;
}
.form-check.form-check-group input[type=radio]:checked + label::before {
  right: 0;
}
.form-check.form-check-group input.semi-checked:not(:checked) + label::before {
  right: 4px;
  left: auto;
}
.form-check input.semi-checked:not(:checked) + label::before {
  top: 12px;
  left: 4px;
  width: 12px;
  height: 2px;
  border-width: 0;
  border-style: none;
  border-color: transparent;
  opacity: 1;
  background: var(--bsi-color-background-inverse);
  transform: none;
  backface-visibility: hidden;
}
.form-check input.semi-checked:not(:checked) + label::after {
  z-index: 0;
  border-color: var(--bsi-form-checked-color);
  background-color: var(--bsi-form-checked-color);
}

.form-check-inline {
  display: inline-block;
}
.form-check-inline:not(:last-child) {
  margin-right: var(--bsi-spacing-m);
}

@media (prefers-reduced-motion: reduce) {
  fieldset legend,
  .form-group label,
  textarea,
  .form-check [type=checkbox],
  .form-check [type=radio],
  .form-check [type=checkbox] + label::after,
  .form-check [type=checkbox] + label::before,
  .form-check [type=radio] + label::after,
  .form-check [type=radio] + label::before,
  .toggles label input[type=checkbox] + .lever::before,
  .toggles label input[type=checkbox] + .lever::after {
    transition: none !important;
  }
}
.form-control-plaintext {
  border: 0;
  --bsi-form-control-border-color: transparent;
  --bsi-form-control-border-radius: 0;
  --bsi-form-control-background-color: transparent;
  --bsi-form-control-spacing: 0;
}
.form-control-plaintext:focus {
  outline: 0;
  box-shadow: none !important;
}
.form-control-plaintext + label {
  cursor: text;
}

.form-control {
  background-repeat: no-repeat;
  background-position: center right;
  background-size: 45px 30%;
}
.form-control:disabled {
  cursor: not-allowed;
  background: var(--bsi-color-background-disabled);
  border: 0;
  color: var(--bsi-color-text-disabled);
}
.was-validated .form-control:valid, .form-control.is-valid {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23008055' viewBox='0 0 192 512'%3E%3Cpath d='M435.848 83.466L172.804 346.51l-96.652-96.652c-4.686-4.686-12.284-4.686-16.971 0l-28.284 28.284c-4.686 4.686-4.686 12.284 0 16.971l133.421 133.421c4.686 4.686 12.284 4.686 16.971 0l299.813-299.813c4.686-4.686 4.686-12.284 0-16.971l-28.284-28.284c-4.686-4.686-12.284-4.686-16.97 0z'/%3E%3C/svg%3E");
}
.was-validated .form-control:invalid, .form-control.is-invalid {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23cc334d' viewBox='0 0 384 512'%3E%3Cpath d='M231.6 256l130.1-130.1c4.7-4.7 4.7-12.3 0-17l-22.6-22.6c-4.7-4.7-12.3-4.7-17 0L192 216.4 61.9 86.3c-4.7-4.7-12.3-4.7-17 0l-22.6 22.6c-4.7 4.7-4.7 12.3 0 17L152.4 256 22.3 386.1c-4.7 4.7-4.7 12.3 0 17l22.6 22.6c4.7 4.7 12.3 4.7 17 0L192 295.6l130.1 130.1c4.7 4.7 12.3 4.7 17 0l22.6-22.6c4.7-4.7 4.7-12.3 0-17L231.6 256z'/%3E%3C/svg%3E");
  border-color: var(--bsi-color-border-danger);
}
.form-control.warning {
  background-image: url("data:image/svg+xml,%3Csvg%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%0A%3Cpath%20fill-rule%3D%22evenodd%22%20clip-rule%3D%22evenodd%22%20d%3D%22M2%2012C2%206.47715%206.47715%202%2012%202C14.6522%202%2017.1957%203.05357%2019.0711%204.92893C20.9464%206.8043%2022%209.34784%2022%2012C22%2017.5228%2017.5228%2022%2012%2022C6.47715%2022%202%2017.5228%202%2012ZM3%2012C3%2016.9706%207.02944%2021%2012%2021C16.9706%2021%2021%2016.9706%2021%2012C21%207.02944%2016.9706%203%2012%203C7.02944%203%203%207.02944%203%2012ZM11.5%2014.2V5.7H12.7V14.2H11.5ZM12.6%2018.3V16.5H11.4V18.3H12.6Z%22/%3E%0A%3C/svg%3E") no-repeat;
  border-color: var(--bsi-color-border-warning);
}
.form-control.is-valid ~ .warning-feedback {
  display: block;
}

.form-control-sm {
  --bsi-form-control-spacing: var(--bsi-spacing-xxs) var(--bsi-spacing-3xs);
  --bsi-form-control-font-size: var(--bsi-label-font-size);
}
.form-control-sm::file-selector-button {
  padding: 0.25rem 0.5rem;
  margin: -0.25rem -0.5rem;
  margin-inline-end: 0.5rem;
}

.form-control-lg {
  --bsi-form-control-font-size: var(--bsi-lead-font-size);
}
.form-control-lg::file-selector-button {
  padding: 0.5rem 1rem;
  margin: -0.5rem -1rem;
  margin-inline-end: 1rem;
}

textarea.form-control {
  min-height: 2.5rem;
  border: 1px solid hsl(210, 17%, 44%);
}
textarea.form-control-sm {
  min-height: calc(1.5em + 0.5rem);
}
textarea.form-control-lg {
  min-height: calc(1.5em + 1rem);
}

.form-control-color {
  width: 3rem;
  height: 2.5rem;
  padding: 0.375rem;
}
.form-control-color:not(:disabled):not([readonly]) {
  cursor: pointer;
}
.form-control-color::-moz-color-swatch {
  border: 0 !important;
  border-radius: 0;
}
.form-control-color::-webkit-color-swatch {
  border-radius: 0;
}
.form-control-color.form-control-sm {
  height: calc(1.5em + 0.5rem);
}
.form-control-color.form-control-lg {
  height: calc(1.5em + 1rem);
}

.form-check-reverse {
  padding-right: 1.5em;
  padding-left: 0;
  text-align: right;
}
.form-check-reverse .form-check-input {
  float: right;
  margin-right: -1.5em;
  margin-left: 0;
}

.form-check-input {
  width: 1em;
  height: 1em;
  margin-top: 0.25em;
  vertical-align: top;
  background-color: hsl(0, 0%, 100%);
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  border: 1px solid rgba(0, 0, 0, 0.25);
  appearance: none;
  print-color-adjust: exact;
}
.form-check-input[type=checkbox] {
  border-radius: 0.25em;
}
.form-check-input[type=radio] {
  border-radius: 50%;
}
.form-check-input:active {
  filter: brightness(90%);
}
.form-check-input:focus {
  border-color: hsl(210, 17%, 44%);
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(0, 102, 204, 0.25);
}
.form-check-input:checked {
  background-color: hsl(210, 100%, 40%);
  border-color: hsl(210, 100%, 40%);
}
.form-check-input:checked[type=checkbox] {
  background-image: escape-svg(url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path fill='none' stroke='hsl(0, 0%, 100%)' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='m6 10 3 3 6-6'/></svg>"));
}
.form-check-input:checked[type=radio] {
  background-image: escape-svg(url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'><circle r='2' fill='hsl(0, 0%, 100%)'/></svg>"));
}
.form-check-input[type=checkbox]:indeterminate {
  background-color: hsl(210, 100%, 40%);
  border-color: hsl(210, 100%, 40%);
  background-image: escape-svg(url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path fill='none' stroke='hsl(0, 0%, 100%)' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10h8'/></svg>"));
}
.form-check-input:disabled {
  pointer-events: none;
  filter: none;
  opacity: 0.5;
}
.form-check-input[disabled] ~ .form-check-label, .form-check-input:disabled ~ .form-check-label {
  cursor: default;
  opacity: 0.5;
}

.form-switch {
  padding-left: 2.5em;
}
.form-switch .form-check-input {
  width: 2em;
  margin-left: -2.5em;
  background-image: escape-svg(url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'><circle r='3' fill='rgba(0, 0, 0, 0.25)'/></svg>"));
  background-position: left center;
  border-radius: 2em;
  transition: background-position 0.15s ease-in-out;
}
@media (prefers-reduced-motion: reduce) {
  .form-switch .form-check-input {
    transition: none;
  }
}
.form-switch .form-check-input:focus {
  background-image: escape-svg(url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'><circle r='3' fill='hsl(210, 17%, 44%)'/></svg>"));
}
.form-switch .form-check-input:checked {
  background-position: right center;
  background-image: escape-svg(url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'><circle r='3' fill='hsl(0, 0%, 100%)'/></svg>"));
}
.form-switch.form-check-reverse {
  padding-right: 2.5em;
  padding-left: 0;
}
.form-switch.form-check-reverse .form-check-input {
  margin-right: -2.5em;
  margin-left: 0;
}

.form-check-inline {
  display: inline-block;
  margin-right: 1rem;
}

.btn-check {
  position: absolute;
  clip: rect(0, 0, 0, 0);
  pointer-events: none;
}
.btn-check[disabled] + .btn, .btn-check:disabled + .btn {
  pointer-events: none;
  filter: none;
  opacity: 0.65;
}

.it-transfer-wrapper {
  border: 1px solid hsl(210, 3.6255162919%, 78.21%);
  border-radius: 4px;
}
.it-transfer-wrapper .transfer-scroll {
  height: 240px;
  overflow-x: hidden;
  margin-bottom: 24px;
}
.it-transfer-wrapper .form-check {
  padding: 0 24px;
  position: relative;
}
.it-transfer-wrapper .form-check input.semi-checked:not(:checked) + label::before {
  top: 13px;
  left: 4px;
  width: 12px;
  height: 2px;
  border-style: none;
  border-width: 0;
  border-color: transparent;
  transform: none;
  backface-visibility: hidden;
  opacity: 1;
  background: #fff;
}
.it-transfer-wrapper .form-check input.semi-checked:not(:checked) + label::after {
  border-color: hsl(210, 73.9130434783%, 48.3%);
  background-color: hsl(210, 73.9130434783%, 48.3%);
  z-index: 0;
}
.it-transfer-wrapper .transfer-header {
  font-weight: bold;
}
.it-transfer-wrapper .transfer-header:after {
  height: 1px;
  background: hsl(210, 3.6255162919%, 78.21%);
  width: 65px;
  content: "";
  display: block;
  margin-left: 62px;
}
.it-transfer-wrapper .transfer-header .form-check {
  padding: 24px 24px 8px;
  margin-top: 0;
}
.it-transfer-wrapper .transfer-header .form-check [type=checkbox] + label {
  display: flex;
  justify-content: space-between;
}
.it-transfer-wrapper .transfer-header .form-check .descr {
  display: block;
  font-size: 0.75rem;
  color: hsl(210, 25%, 35.2%);
  font-weight: normal;
}

.it-transfer-buttons {
  display: flex;
  justify-content: center;
}
.it-transfer-buttons a {
  display: flex;
  width: 40px;
  height: 40px;
  border: 1px solid hsl(210, 3.6255162919%, 78.21%);
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  margin: 24px 8px;
  cursor: default;
  pointer-events: none;
}
.it-transfer-buttons a .icon {
  fill: hsl(210, 3.6255162919%, 78.21%);
}
.it-transfer-buttons a.transfer, .it-transfer-buttons a.backtransfer {
  transform: rotate(90deg);
}
.it-transfer-buttons a.active {
  border-color: hsl(210, 73.9130434783%, 48.3%);
  cursor: pointer;
  pointer-events: all;
}
.it-transfer-buttons a.active .icon {
  fill: hsl(210, 73.9130434783%, 48.3%);
}

@media (min-width: 768px) {
  .it-transfer-buttons {
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
  }
  .it-transfer-buttons a {
    display: inline-flex;
    width: 40px;
    height: 40px;
    border: 1px solid hsl(210, 3.6255162919%, 78.21%);
    border-radius: 100px;
    align-items: center;
    justify-content: center;
    margin: 8px 8px;
    align-self: center;
  }
  .it-transfer-buttons a.transfer, .it-transfer-buttons a.backtransfer {
    transform: none;
  }
}
.form-feedback {
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.75rem;
}
.form-feedback.just-validate-error-label {
  color: var(--bsi-color-text-danger);
}

.input-group-text:has(~ [data-focus-mouse=true]:not(.btn)),
[data-focus-mouse=true]:not(.btn) ~ .input-group-text,
button:has(~ [data-focus-mouse=true]:not(.btn)),
[data-focus-mouse=true]:not(.btn) + button {
  border-color: inherit !important;
  box-shadow: none !important;
  outline: none !important;
}

.input-group-text:has(~ .is-invalid),
.is-invalid ~ .input-group-text,
button:has(~ .is-invalid),
.is-invalid + button {
  border-color: var(--bsi-color-border-danger) !important;
}

.sr-only-justvalidate-bi {
  display: none;
}

.just-validate-success-field {
  border-color: var(--bsi-color-border-success) !important;
  padding-right: calc(1.5em + 0.75rem) !important;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23008055' viewBox='0 0 192 512'%3E%3Cpath d='M435.848 83.466L172.804 346.51l-96.652-96.652c-4.686-4.686-12.284-4.686-16.971 0l-28.284 28.284c-4.686 4.686-4.686 12.284 0 16.971l133.421 133.421c4.686 4.686 12.284 4.686 16.971 0l299.813-299.813c4.686-4.686 4.686-12.284 0-16.971l-28.284-28.284c-4.686-4.686-12.284-4.686-16.97 0z'/%3E%3C/svg%3E");
}

.input-group-text:has(~ .just-validate-success-field),
.just-validate-success-field ~ .input-group-text,
button:has(~ .just-validate-success-field),
.just-validate-success-field + button {
  border-color: var(--bsi-color-border-success);
}

.just-validate-success-field + .input-group-text.align-buttons,
.is-invalid + .input-group-text.align-buttons {
  right: 30px;
}

.is-invalid + .input-group-text.align-buttons {
  bottom: 22px;
}

.autocomplete__wrapper .form-feedback.just-validate-error-label {
  position: absolute;
}

textarea.form-control {
  background-position: top 0.3em right 0.3em !important;
  background-size: 37px 30% !important;
}
textarea.is-invalid {
  border-bottom-style: solid;
  border-bottom-width: 1px;
}
textarea.just-validate-success-field {
  border-bottom-style: solid;
  border-bottom-width: 1px;
}

input[type=checkbox].is-invalid,
input[type=radio].is-invalid {
  --bsi-form-checkbox-border-color: var(--bsi-color-border-danger);
}

select.is-invalid {
  border: 1px solid var(--bsi-color-border-danger);
}
select.just-validate-success-field {
  border: 1px solid var(--bsi-color-border-success);
}

.row {
  --bsi-gutter-x: 24px;
  --bsi-gutter-y: 0;
  display: flex;
  flex-wrap: wrap;
  margin-top: calc(-1 * var(--bsi-gutter-y));
  margin-right: calc(-0.5 * var(--bsi-gutter-x));
  margin-left: calc(-0.5 * var(--bsi-gutter-x));
}
.row > * {
  flex-shrink: 0;
  width: 100%;
  max-width: 100%;
  padding-right: calc(var(--bsi-gutter-x) * 0.5);
  padding-left: calc(var(--bsi-gutter-x) * 0.5);
  margin-top: var(--bsi-gutter-y);
}

.col {
  flex: 1 0 0%;
}

.row-cols-auto > * {
  flex: 0 0 auto;
  width: auto;
}

.row-cols-1 > * {
  flex: 0 0 auto;
  width: 100%;
}

.row-cols-2 > * {
  flex: 0 0 auto;
  width: 50%;
}

.row-cols-3 > * {
  flex: 0 0 auto;
  width: 33.3333333333%;
}

.row-cols-4 > * {
  flex: 0 0 auto;
  width: 25%;
}

.row-cols-5 > * {
  flex: 0 0 auto;
  width: 20%;
}

.row-cols-6 > * {
  flex: 0 0 auto;
  width: 16.6666666667%;
}

.col-auto {
  flex: 0 0 auto;
  width: auto;
}

.col-1 {
  flex: 0 0 auto;
  width: 8.33333333%;
}

.col-2 {
  flex: 0 0 auto;
  width: 16.66666667%;
}

.col-3 {
  flex: 0 0 auto;
  width: 25%;
}

.col-4 {
  flex: 0 0 auto;
  width: 33.33333333%;
}

.col-5 {
  flex: 0 0 auto;
  width: 41.66666667%;
}

.col-6 {
  flex: 0 0 auto;
  width: 50%;
}

.col-7 {
  flex: 0 0 auto;
  width: 58.33333333%;
}

.col-8 {
  flex: 0 0 auto;
  width: 66.66666667%;
}

.col-9 {
  flex: 0 0 auto;
  width: 75%;
}

.col-10 {
  flex: 0 0 auto;
  width: 83.33333333%;
}

.col-11 {
  flex: 0 0 auto;
  width: 91.66666667%;
}

.col-12 {
  flex: 0 0 auto;
  width: 100%;
}

.offset-1 {
  margin-left: 8.33333333%;
}

.offset-2 {
  margin-left: 16.66666667%;
}

.offset-3 {
  margin-left: 25%;
}

.offset-4 {
  margin-left: 33.33333333%;
}

.offset-5 {
  margin-left: 41.66666667%;
}

.offset-6 {
  margin-left: 50%;
}

.offset-7 {
  margin-left: 58.33333333%;
}

.offset-8 {
  margin-left: 66.66666667%;
}

.offset-9 {
  margin-left: 75%;
}

.offset-10 {
  margin-left: 83.33333333%;
}

.offset-11 {
  margin-left: 91.66666667%;
}

.g-0,
.gx-0 {
  --bsi-gutter-x: 0;
}

.g-0,
.gy-0 {
  --bsi-gutter-y: 0;
}

.g-1,
.gx-1 {
  --bsi-gutter-x: 0.25rem;
}

.g-1,
.gy-1 {
  --bsi-gutter-y: 0.25rem;
}

.g-2,
.gx-2 {
  --bsi-gutter-x: 0.5rem;
}

.g-2,
.gy-2 {
  --bsi-gutter-y: 0.5rem;
}

.g-3,
.gx-3 {
  --bsi-gutter-x: 1rem;
}

.g-3,
.gy-3 {
  --bsi-gutter-y: 1rem;
}

.g-4,
.gx-4 {
  --bsi-gutter-x: 1.5rem;
}

.g-4,
.gy-4 {
  --bsi-gutter-y: 1.5rem;
}

.g-5,
.gx-5 {
  --bsi-gutter-x: 3rem;
}

.g-5,
.gy-5 {
  --bsi-gutter-y: 3rem;
}

@media (min-width: 576px) {
  .col-sm {
    flex: 1 0 0%;
  }
  .row-cols-sm-auto > * {
    flex: 0 0 auto;
    width: auto;
  }
  .row-cols-sm-1 > * {
    flex: 0 0 auto;
    width: 100%;
  }
  .row-cols-sm-2 > * {
    flex: 0 0 auto;
    width: 50%;
  }
  .row-cols-sm-3 > * {
    flex: 0 0 auto;
    width: 33.3333333333%;
  }
  .row-cols-sm-4 > * {
    flex: 0 0 auto;
    width: 25%;
  }
  .row-cols-sm-5 > * {
    flex: 0 0 auto;
    width: 20%;
  }
  .row-cols-sm-6 > * {
    flex: 0 0 auto;
    width: 16.6666666667%;
  }
  .col-sm-auto {
    flex: 0 0 auto;
    width: auto;
  }
  .col-sm-1 {
    flex: 0 0 auto;
    width: 8.33333333%;
  }
  .col-sm-2 {
    flex: 0 0 auto;
    width: 16.66666667%;
  }
  .col-sm-3 {
    flex: 0 0 auto;
    width: 25%;
  }
  .col-sm-4 {
    flex: 0 0 auto;
    width: 33.33333333%;
  }
  .col-sm-5 {
    flex: 0 0 auto;
    width: 41.66666667%;
  }
  .col-sm-6 {
    flex: 0 0 auto;
    width: 50%;
  }
  .col-sm-7 {
    flex: 0 0 auto;
    width: 58.33333333%;
  }
  .col-sm-8 {
    flex: 0 0 auto;
    width: 66.66666667%;
  }
  .col-sm-9 {
    flex: 0 0 auto;
    width: 75%;
  }
  .col-sm-10 {
    flex: 0 0 auto;
    width: 83.33333333%;
  }
  .col-sm-11 {
    flex: 0 0 auto;
    width: 91.66666667%;
  }
  .col-sm-12 {
    flex: 0 0 auto;
    width: 100%;
  }
  .offset-sm-0 {
    margin-left: 0;
  }
  .offset-sm-1 {
    margin-left: 8.33333333%;
  }
  .offset-sm-2 {
    margin-left: 16.66666667%;
  }
  .offset-sm-3 {
    margin-left: 25%;
  }
  .offset-sm-4 {
    margin-left: 33.33333333%;
  }
  .offset-sm-5 {
    margin-left: 41.66666667%;
  }
  .offset-sm-6 {
    margin-left: 50%;
  }
  .offset-sm-7 {
    margin-left: 58.33333333%;
  }
  .offset-sm-8 {
    margin-left: 66.66666667%;
  }
  .offset-sm-9 {
    margin-left: 75%;
  }
  .offset-sm-10 {
    margin-left: 83.33333333%;
  }
  .offset-sm-11 {
    margin-left: 91.66666667%;
  }
  .g-sm-0,
  .gx-sm-0 {
    --bsi-gutter-x: 0;
  }
  .g-sm-0,
  .gy-sm-0 {
    --bsi-gutter-y: 0;
  }
  .g-sm-1,
  .gx-sm-1 {
    --bsi-gutter-x: 0.25rem;
  }
  .g-sm-1,
  .gy-sm-1 {
    --bsi-gutter-y: 0.25rem;
  }
  .g-sm-2,
  .gx-sm-2 {
    --bsi-gutter-x: 0.5rem;
  }
  .g-sm-2,
  .gy-sm-2 {
    --bsi-gutter-y: 0.5rem;
  }
  .g-sm-3,
  .gx-sm-3 {
    --bsi-gutter-x: 1rem;
  }
  .g-sm-3,
  .gy-sm-3 {
    --bsi-gutter-y: 1rem;
  }
  .g-sm-4,
  .gx-sm-4 {
    --bsi-gutter-x: 1.5rem;
  }
  .g-sm-4,
  .gy-sm-4 {
    --bsi-gutter-y: 1.5rem;
  }
  .g-sm-5,
  .gx-sm-5 {
    --bsi-gutter-x: 3rem;
  }
  .g-sm-5,
  .gy-sm-5 {
    --bsi-gutter-y: 3rem;
  }
}
@media (min-width: 768px) {
  .col-md {
    flex: 1 0 0%;
  }
  .row-cols-md-auto > * {
    flex: 0 0 auto;
    width: auto;
  }
  .row-cols-md-1 > * {
    flex: 0 0 auto;
    width: 100%;
  }
  .row-cols-md-2 > * {
    flex: 0 0 auto;
    width: 50%;
  }
  .row-cols-md-3 > * {
    flex: 0 0 auto;
    width: 33.3333333333%;
  }
  .row-cols-md-4 > * {
    flex: 0 0 auto;
    width: 25%;
  }
  .row-cols-md-5 > * {
    flex: 0 0 auto;
    width: 20%;
  }
  .row-cols-md-6 > * {
    flex: 0 0 auto;
    width: 16.6666666667%;
  }
  .col-md-auto {
    flex: 0 0 auto;
    width: auto;
  }
  .col-md-1 {
    flex: 0 0 auto;
    width: 8.33333333%;
  }
  .col-md-2 {
    flex: 0 0 auto;
    width: 16.66666667%;
  }
  .col-md-3 {
    flex: 0 0 auto;
    width: 25%;
  }
  .col-md-4 {
    flex: 0 0 auto;
    width: 33.33333333%;
  }
  .col-md-5 {
    flex: 0 0 auto;
    width: 41.66666667%;
  }
  .col-md-6 {
    flex: 0 0 auto;
    width: 50%;
  }
  .col-md-7 {
    flex: 0 0 auto;
    width: 58.33333333%;
  }
  .col-md-8 {
    flex: 0 0 auto;
    width: 66.66666667%;
  }
  .col-md-9 {
    flex: 0 0 auto;
    width: 75%;
  }
  .col-md-10 {
    flex: 0 0 auto;
    width: 83.33333333%;
  }
  .col-md-11 {
    flex: 0 0 auto;
    width: 91.66666667%;
  }
  .col-md-12 {
    flex: 0 0 auto;
    width: 100%;
  }
  .offset-md-0 {
    margin-left: 0;
  }
  .offset-md-1 {
    margin-left: 8.33333333%;
  }
  .offset-md-2 {
    margin-left: 16.66666667%;
  }
  .offset-md-3 {
    margin-left: 25%;
  }
  .offset-md-4 {
    margin-left: 33.33333333%;
  }
  .offset-md-5 {
    margin-left: 41.66666667%;
  }
  .offset-md-6 {
    margin-left: 50%;
  }
  .offset-md-7 {
    margin-left: 58.33333333%;
  }
  .offset-md-8 {
    margin-left: 66.66666667%;
  }
  .offset-md-9 {
    margin-left: 75%;
  }
  .offset-md-10 {
    margin-left: 83.33333333%;
  }
  .offset-md-11 {
    margin-left: 91.66666667%;
  }
  .g-md-0,
  .gx-md-0 {
    --bsi-gutter-x: 0;
  }
  .g-md-0,
  .gy-md-0 {
    --bsi-gutter-y: 0;
  }
  .g-md-1,
  .gx-md-1 {
    --bsi-gutter-x: 0.25rem;
  }
  .g-md-1,
  .gy-md-1 {
    --bsi-gutter-y: 0.25rem;
  }
  .g-md-2,
  .gx-md-2 {
    --bsi-gutter-x: 0.5rem;
  }
  .g-md-2,
  .gy-md-2 {
    --bsi-gutter-y: 0.5rem;
  }
  .g-md-3,
  .gx-md-3 {
    --bsi-gutter-x: 1rem;
  }
  .g-md-3,
  .gy-md-3 {
    --bsi-gutter-y: 1rem;
  }
  .g-md-4,
  .gx-md-4 {
    --bsi-gutter-x: 1.5rem;
  }
  .g-md-4,
  .gy-md-4 {
    --bsi-gutter-y: 1.5rem;
  }
  .g-md-5,
  .gx-md-5 {
    --bsi-gutter-x: 3rem;
  }
  .g-md-5,
  .gy-md-5 {
    --bsi-gutter-y: 3rem;
  }
}
@media (min-width: 992px) {
  .col-lg {
    flex: 1 0 0%;
  }
  .row-cols-lg-auto > * {
    flex: 0 0 auto;
    width: auto;
  }
  .row-cols-lg-1 > * {
    flex: 0 0 auto;
    width: 100%;
  }
  .row-cols-lg-2 > * {
    flex: 0 0 auto;
    width: 50%;
  }
  .row-cols-lg-3 > * {
    flex: 0 0 auto;
    width: 33.3333333333%;
  }
  .row-cols-lg-4 > * {
    flex: 0 0 auto;
    width: 25%;
  }
  .row-cols-lg-5 > * {
    flex: 0 0 auto;
    width: 20%;
  }
  .row-cols-lg-6 > * {
    flex: 0 0 auto;
    width: 16.6666666667%;
  }
  .col-lg-auto {
    flex: 0 0 auto;
    width: auto;
  }
  .col-lg-1 {
    flex: 0 0 auto;
    width: 8.33333333%;
  }
  .col-lg-2 {
    flex: 0 0 auto;
    width: 16.66666667%;
  }
  .col-lg-3 {
    flex: 0 0 auto;
    width: 25%;
  }
  .col-lg-4 {
    flex: 0 0 auto;
    width: 33.33333333%;
  }
  .col-lg-5 {
    flex: 0 0 auto;
    width: 41.66666667%;
  }
  .col-lg-6 {
    flex: 0 0 auto;
    width: 50%;
  }
  .col-lg-7 {
    flex: 0 0 auto;
    width: 58.33333333%;
  }
  .col-lg-8 {
    flex: 0 0 auto;
    width: 66.66666667%;
  }
  .col-lg-9 {
    flex: 0 0 auto;
    width: 75%;
  }
  .col-lg-10 {
    flex: 0 0 auto;
    width: 83.33333333%;
  }
  .col-lg-11 {
    flex: 0 0 auto;
    width: 91.66666667%;
  }
  .col-lg-12 {
    flex: 0 0 auto;
    width: 100%;
  }
  .offset-lg-0 {
    margin-left: 0;
  }
  .offset-lg-1 {
    margin-left: 8.33333333%;
  }
  .offset-lg-2 {
    margin-left: 16.66666667%;
  }
  .offset-lg-3 {
    margin-left: 25%;
  }
  .offset-lg-4 {
    margin-left: 33.33333333%;
  }
  .offset-lg-5 {
    margin-left: 41.66666667%;
  }
  .offset-lg-6 {
    margin-left: 50%;
  }
  .offset-lg-7 {
    margin-left: 58.33333333%;
  }
  .offset-lg-8 {
    margin-left: 66.66666667%;
  }
  .offset-lg-9 {
    margin-left: 75%;
  }
  .offset-lg-10 {
    margin-left: 83.33333333%;
  }
  .offset-lg-11 {
    margin-left: 91.66666667%;
  }
  .g-lg-0,
  .gx-lg-0 {
    --bsi-gutter-x: 0;
  }
  .g-lg-0,
  .gy-lg-0 {
    --bsi-gutter-y: 0;
  }
  .g-lg-1,
  .gx-lg-1 {
    --bsi-gutter-x: 0.25rem;
  }
  .g-lg-1,
  .gy-lg-1 {
    --bsi-gutter-y: 0.25rem;
  }
  .g-lg-2,
  .gx-lg-2 {
    --bsi-gutter-x: 0.5rem;
  }
  .g-lg-2,
  .gy-lg-2 {
    --bsi-gutter-y: 0.5rem;
  }
  .g-lg-3,
  .gx-lg-3 {
    --bsi-gutter-x: 1rem;
  }
  .g-lg-3,
  .gy-lg-3 {
    --bsi-gutter-y: 1rem;
  }
  .g-lg-4,
  .gx-lg-4 {
    --bsi-gutter-x: 1.5rem;
  }
  .g-lg-4,
  .gy-lg-4 {
    --bsi-gutter-y: 1.5rem;
  }
  .g-lg-5,
  .gx-lg-5 {
    --bsi-gutter-x: 3rem;
  }
  .g-lg-5,
  .gy-lg-5 {
    --bsi-gutter-y: 3rem;
  }
}
@media (min-width: 1200px) {
  .col-xl {
    flex: 1 0 0%;
  }
  .row-cols-xl-auto > * {
    flex: 0 0 auto;
    width: auto;
  }
  .row-cols-xl-1 > * {
    flex: 0 0 auto;
    width: 100%;
  }
  .row-cols-xl-2 > * {
    flex: 0 0 auto;
    width: 50%;
  }
  .row-cols-xl-3 > * {
    flex: 0 0 auto;
    width: 33.3333333333%;
  }
  .row-cols-xl-4 > * {
    flex: 0 0 auto;
    width: 25%;
  }
  .row-cols-xl-5 > * {
    flex: 0 0 auto;
    width: 20%;
  }
  .row-cols-xl-6 > * {
    flex: 0 0 auto;
    width: 16.6666666667%;
  }
  .col-xl-auto {
    flex: 0 0 auto;
    width: auto;
  }
  .col-xl-1 {
    flex: 0 0 auto;
    width: 8.33333333%;
  }
  .col-xl-2 {
    flex: 0 0 auto;
    width: 16.66666667%;
  }
  .col-xl-3 {
    flex: 0 0 auto;
    width: 25%;
  }
  .col-xl-4 {
    flex: 0 0 auto;
    width: 33.33333333%;
  }
  .col-xl-5 {
    flex: 0 0 auto;
    width: 41.66666667%;
  }
  .col-xl-6 {
    flex: 0 0 auto;
    width: 50%;
  }
  .col-xl-7 {
    flex: 0 0 auto;
    width: 58.33333333%;
  }
  .col-xl-8 {
    flex: 0 0 auto;
    width: 66.66666667%;
  }
  .col-xl-9 {
    flex: 0 0 auto;
    width: 75%;
  }
  .col-xl-10 {
    flex: 0 0 auto;
    width: 83.33333333%;
  }
  .col-xl-11 {
    flex: 0 0 auto;
    width: 91.66666667%;
  }
  .col-xl-12 {
    flex: 0 0 auto;
    width: 100%;
  }
  .offset-xl-0 {
    margin-left: 0;
  }
  .offset-xl-1 {
    margin-left: 8.33333333%;
  }
  .offset-xl-2 {
    margin-left: 16.66666667%;
  }
  .offset-xl-3 {
    margin-left: 25%;
  }
  .offset-xl-4 {
    margin-left: 33.33333333%;
  }
  .offset-xl-5 {
    margin-left: 41.66666667%;
  }
  .offset-xl-6 {
    margin-left: 50%;
  }
  .offset-xl-7 {
    margin-left: 58.33333333%;
  }
  .offset-xl-8 {
    margin-left: 66.66666667%;
  }
  .offset-xl-9 {
    margin-left: 75%;
  }
  .offset-xl-10 {
    margin-left: 83.33333333%;
  }
  .offset-xl-11 {
    margin-left: 91.66666667%;
  }
  .g-xl-0,
  .gx-xl-0 {
    --bsi-gutter-x: 0;
  }
  .g-xl-0,
  .gy-xl-0 {
    --bsi-gutter-y: 0;
  }
  .g-xl-1,
  .gx-xl-1 {
    --bsi-gutter-x: 0.25rem;
  }
  .g-xl-1,
  .gy-xl-1 {
    --bsi-gutter-y: 0.25rem;
  }
  .g-xl-2,
  .gx-xl-2 {
    --bsi-gutter-x: 0.5rem;
  }
  .g-xl-2,
  .gy-xl-2 {
    --bsi-gutter-y: 0.5rem;
  }
  .g-xl-3,
  .gx-xl-3 {
    --bsi-gutter-x: 1rem;
  }
  .g-xl-3,
  .gy-xl-3 {
    --bsi-gutter-y: 1rem;
  }
  .g-xl-4,
  .gx-xl-4 {
    --bsi-gutter-x: 1.5rem;
  }
  .g-xl-4,
  .gy-xl-4 {
    --bsi-gutter-y: 1.5rem;
  }
  .g-xl-5,
  .gx-xl-5 {
    --bsi-gutter-x: 3rem;
  }
  .g-xl-5,
  .gy-xl-5 {
    --bsi-gutter-y: 3rem;
  }
}
@media (min-width: 1400px) {
  .col-xxl {
    flex: 1 0 0%;
  }
  .row-cols-xxl-auto > * {
    flex: 0 0 auto;
    width: auto;
  }
  .row-cols-xxl-1 > * {
    flex: 0 0 auto;
    width: 100%;
  }
  .row-cols-xxl-2 > * {
    flex: 0 0 auto;
    width: 50%;
  }
  .row-cols-xxl-3 > * {
    flex: 0 0 auto;
    width: 33.3333333333%;
  }
  .row-cols-xxl-4 > * {
    flex: 0 0 auto;
    width: 25%;
  }
  .row-cols-xxl-5 > * {
    flex: 0 0 auto;
    width: 20%;
  }
  .row-cols-xxl-6 > * {
    flex: 0 0 auto;
    width: 16.6666666667%;
  }
  .col-xxl-auto {
    flex: 0 0 auto;
    width: auto;
  }
  .col-xxl-1 {
    flex: 0 0 auto;
    width: 8.33333333%;
  }
  .col-xxl-2 {
    flex: 0 0 auto;
    width: 16.66666667%;
  }
  .col-xxl-3 {
    flex: 0 0 auto;
    width: 25%;
  }
  .col-xxl-4 {
    flex: 0 0 auto;
    width: 33.33333333%;
  }
  .col-xxl-5 {
    flex: 0 0 auto;
    width: 41.66666667%;
  }
  .col-xxl-6 {
    flex: 0 0 auto;
    width: 50%;
  }
  .col-xxl-7 {
    flex: 0 0 auto;
    width: 58.33333333%;
  }
  .col-xxl-8 {
    flex: 0 0 auto;
    width: 66.66666667%;
  }
  .col-xxl-9 {
    flex: 0 0 auto;
    width: 75%;
  }
  .col-xxl-10 {
    flex: 0 0 auto;
    width: 83.33333333%;
  }
  .col-xxl-11 {
    flex: 0 0 auto;
    width: 91.66666667%;
  }
  .col-xxl-12 {
    flex: 0 0 auto;
    width: 100%;
  }
  .offset-xxl-0 {
    margin-left: 0;
  }
  .offset-xxl-1 {
    margin-left: 8.33333333%;
  }
  .offset-xxl-2 {
    margin-left: 16.66666667%;
  }
  .offset-xxl-3 {
    margin-left: 25%;
  }
  .offset-xxl-4 {
    margin-left: 33.33333333%;
  }
  .offset-xxl-5 {
    margin-left: 41.66666667%;
  }
  .offset-xxl-6 {
    margin-left: 50%;
  }
  .offset-xxl-7 {
    margin-left: 58.33333333%;
  }
  .offset-xxl-8 {
    margin-left: 66.66666667%;
  }
  .offset-xxl-9 {
    margin-left: 75%;
  }
  .offset-xxl-10 {
    margin-left: 83.33333333%;
  }
  .offset-xxl-11 {
    margin-left: 91.66666667%;
  }
  .g-xxl-0,
  .gx-xxl-0 {
    --bsi-gutter-x: 0;
  }
  .g-xxl-0,
  .gy-xxl-0 {
    --bsi-gutter-y: 0;
  }
  .g-xxl-1,
  .gx-xxl-1 {
    --bsi-gutter-x: 0.25rem;
  }
  .g-xxl-1,
  .gy-xxl-1 {
    --bsi-gutter-y: 0.25rem;
  }
  .g-xxl-2,
  .gx-xxl-2 {
    --bsi-gutter-x: 0.5rem;
  }
  .g-xxl-2,
  .gy-xxl-2 {
    --bsi-gutter-y: 0.5rem;
  }
  .g-xxl-3,
  .gx-xxl-3 {
    --bsi-gutter-x: 1rem;
  }
  .g-xxl-3,
  .gy-xxl-3 {
    --bsi-gutter-y: 1rem;
  }
  .g-xxl-4,
  .gx-xxl-4 {
    --bsi-gutter-x: 1.5rem;
  }
  .g-xxl-4,
  .gy-xxl-4 {
    --bsi-gutter-y: 1.5rem;
  }
  .g-xxl-5,
  .gx-xxl-5 {
    --bsi-gutter-x: 3rem;
  }
  .g-xxl-5,
  .gy-xxl-5 {
    --bsi-gutter-y: 3rem;
  }
}
.row.variable-gutters {
  margin-right: -12px;
  margin-left: -12px;
}
.row.variable-gutters > .col,
.row.variable-gutters > [class*=col-] {
  padding-right: 12px;
  padding-left: 12px;
}
.row.variable-gutters {
  margin-right: -6px;
  margin-left: -6px;
}
.row.variable-gutters > .col,
.row.variable-gutters > [class*=col-] {
  padding-right: 6px;
  padding-left: 6px;
}
@media (min-width: 576px) {
  .row.variable-gutters {
    margin-right: -6px;
    margin-left: -6px;
  }
  .row.variable-gutters > .col,
  .row.variable-gutters > [class*=col-] {
    padding-right: 6px;
    padding-left: 6px;
  }
}
@media (min-width: 768px) {
  .row.variable-gutters {
    margin-right: -10px;
    margin-left: -10px;
  }
  .row.variable-gutters > .col,
  .row.variable-gutters > [class*=col-] {
    padding-right: 10px;
    padding-left: 10px;
  }
}
@media (min-width: 992px) {
  .row.variable-gutters {
    margin-right: -12px;
    margin-left: -12px;
  }
  .row.variable-gutters > .col,
  .row.variable-gutters > [class*=col-] {
    padding-right: 12px;
    padding-left: 12px;
  }
}
@media (min-width: 1200px) {
  .row.variable-gutters {
    margin-right: -12px;
    margin-left: -12px;
  }
  .row.variable-gutters > .col,
  .row.variable-gutters > [class*=col-] {
    padding-right: 12px;
    padding-left: 12px;
  }
}
@media (min-width: 1400px) {
  .row.variable-gutters {
    margin-right: -14px;
    margin-left: -14px;
  }
  .row.variable-gutters > .col,
  .row.variable-gutters > [class*=col-] {
    padding-right: 14px;
    padding-left: 14px;
  }
}

.row.row-column-border > [class^=col-] {
  padding-top: var(--bsi-spacing-l);
  padding-bottom: var(--bsi-spacing-l);
  border-top: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
}
.row.row-column-border > [class^=col-]:first-child {
  border: none;
}
.row.row-column-border > [class^=col-] .navbar {
  padding: 0;
}
.row.row-column-border > [class^=col-] .navbar .menu-wrapper .nav-link {
  padding-right: 0;
}
.row.row-column-menu-left > [class^=col-]:first-child {
  padding: var(--bsi-spacing-s) 0;
}
.row.row-column-menu-right > [class^=col-]:last-child {
  padding: var(--bsi-spacing-s) 0;
}
.row.row-card {
  background-color: var(--bsi-color-background-inverse);
}
@media (min-width: 992px) {
  .row.row-column-border {
    margin-top: 1rem;
    border-top: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
  }
  .row.row-column-border > [class^=col-] {
    padding: 3rem 3rem;
    border-top: none;
    border-left: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
  }
  .row.row-column-border > [class^=col-]:first-child {
    border: none;
    padding-left: 0;
  }
  .row.row-column-border > [class^=col-] .navbar {
    padding: 8px 0;
  }
  .row.row-column-border > [class^=col-] .navbar .menu-wrapper {
    padding: 0;
  }
  .row.row-column-menu-left > [class^=col-]:first-child {
    padding: 0;
  }
  .row.row-column-menu-right > [class^=col-]:last-child {
    padding: 0;
  }
}
.row.row-full-width {
  max-width: 100vw;
  margin: 0 calc(-50vw + 50%);
}
.row.row-full-width > * {
  width: 100%;
  display: flex;
  flex-direction: column;
}
.row.row-full-width > * img {
  width: 100%;
  height: 100%;
  max-height: 600px;
  object-fit: cover;
}
.row.row-title {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
@media (min-width: 576px) {
  .row.row-title {
    flex-direction: row;
    align-items: flex-start;
  }
}
.row.row-border h1 {
  border-bottom: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
  padding-bottom: var(--bsi-spacing-s);
  margin-bottom: var(--bsi-spacing-s);
}
.row.row-border h2 {
  border-bottom: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
  padding-bottom: var(--bsi-spacing-s);
  margin-bottom: var(--bsi-spacing-s);
}
.row.row-border h3 {
  border-bottom: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
  padding-bottom: var(--bsi-spacing-s);
  margin-bottom: var(--bsi-spacing-s);
}
.row.row-border h4 {
  border-bottom: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
  padding-bottom: var(--bsi-spacing-s);
  margin-bottom: var(--bsi-spacing-s);
}
.row.row-border h5 {
  border-bottom: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
  padding-bottom: var(--bsi-spacing-s);
  margin-bottom: var(--bsi-spacing-s);
}
.row.row-border h6 {
  border-bottom: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
  padding-bottom: var(--bsi-spacing-s);
  margin-bottom: var(--bsi-spacing-s);
}
@media (min-width: 576px) {
  .row.row-border {
    border-bottom: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
    padding-bottom: var(--bsi-spacing-s);
    margin-bottom: var(--bsi-spacing-s);
  }
  .row.row-border h1 {
    border: none;
    margin: 0;
    padding: 0;
  }
  .row.row-border h2 {
    border: none;
    margin: 0;
    padding: 0;
  }
  .row.row-border h3 {
    border: none;
    margin: 0;
    padding: 0;
  }
  .row.row-border h4 {
    border: none;
    margin: 0;
    padding: 0;
  }
  .row.row-border h5 {
    border: none;
    margin: 0;
    padding: 0;
  }
  .row.row-border h6 {
    border: none;
    margin: 0;
    padding: 0;
  }
}
.row.row-calendar {
  display: block;
}

.sticky-wrapper.is-sticky {
  position: fixed;
}
.sticky-wrapper.is-sticky.navbar-wrapper {
  z-index: 1;
  left: 0;
  right: 0;
  width: auto;
}
.sticky-wrapper.is-sticky.navbar-wrapper .navbar {
  padding-top: var(--bsi-spacing-s);
  padding-bottom: var(--bsi-spacing-s);
  border-top: var(--bsi-border-base) solid var(--bsi-color-border-subtle);
}
.sticky-wrapper.is-sticky.navbar-wrapper.sticky-expanded {
  z-index: auto;
}
@media (min-width: 992px) {
  .sticky-wrapper.is-sticky.navbar-wrapper {
    z-index: auto;
    left: auto;
    right: auto;
    width: unset;
  }
  .sticky-wrapper.is-sticky.navbar-wrapper .navbar {
    border: none;
    background-color: transparent;
    padding: 0;
  }
  .sticky-wrapper.is-sticky.navbar-wrapper .navbar .menu-wrapper {
    padding: 0;
  }
  .sticky-wrapper.is-sticky.navbar-wrapper .navbar .menu-wrapper .nav-link {
    padding-right: 0;
  }
  .sticky-wrapper.is-sticky.navbar-wrapper .navbar.it-bottom-navscroll {
    border: none;
  }
  .sticky-wrapper.is-sticky.navbar-wrapper.at-bottom {
    position: absolute;
    top: auto !important;
    bottom: 0;
  }
}
.sticky-wrapper.navbar-wrapper .navbar.it-top-navscroll, .sticky-wrapper.navbar-wrapper .navbar.it-bottom-navscroll {
  position: relative;
  top: auto;
  left: auto;
  right: auto;
  bottom: auto;
}

:host {
  --it-transfer-border: 1px solid hsl(210, 3.6255162919%, 78.21%);
  --it-transfer-height: 240px;
  --it-transfer-header-color: hsl(210, 25%, 35.2%);
  --it-transfer-btn-size: 40px;
  --it-transfer-btn-active-color: var(--bsi-color-text-primary);
  display: block;
}

.it-transfer-wrapper {
  border: var(--it-transfer-border);
}
.it-transfer-wrapper .transfer-scroll {
  height: var(--it-transfer-height);
}
.it-transfer-wrapper .transfer-header .form-check .descr {
  color: var(--it-transfer-header-color);
}
.it-transfer-wrapper .transfer-group .form-check {
  height: 24px;
}

.it-transfer-buttons a {
  width: var(--it-transfer-btn-size);
  height: var(--it-transfer-btn-size);
  border: var(--it-transfer-border);
  outline: none;
}
.it-transfer-buttons a it-icon {
  display: flex;
}
.it-transfer-buttons a it-icon::part(icon) {
  fill: hsl(210, 3.6255162919%, 78.21%);
}
.it-transfer-buttons a.active {
  border-color: var(--it-transfer-btn-active-color);
}
.it-transfer-buttons a.active it-icon::part(icon) {
  fill: var(--it-transfer-btn-active-color);
}
.it-transfer-buttons a:focus-visible {
  border-color: #000 !important;
  box-shadow: 0 0 0 2px var(--bsi-color-border-inverse), 0 0 0 5px var(--bsi-color-outline-focus) !important;
  outline: 3px solid transparent !important;
  outline-offset: 3px !important;
}

:host([disabled]) {
  opacity: 0.5;
  pointer-events: none;
}

.form-check input:focus:not(:focus-visible) + label {
  box-shadow: none !important;
}

.it-transfer .invalid-feedback {
  display: block;
  margin-top: 8px;
}`;

registerTranslation(translation);
/**
 * A dual-list transfer component that allows moving items between a source and a target list.
 * Integrates with native `<form>` elements via the FormControl API.
 *
 * @fires {CustomEvent<TransferEventDetail>} it-transfer - Fired (cancelable) before items are transferred/reset. Prevent default to cancel.
 * @fires {CustomEvent<{value: string, el: HTMLElement}>} it-change - Fired after the target list changes.
 *
 * @slot - One or more `<it-transfer-item>` elements.
 */
let ItTransfer = class ItTransfer extends FormControl {
    constructor() {
        super(...arguments);
        this.formControlController = new FormControlController(this, {
            value: (control) => control.value,
            name: (control) => control.name,
            checkValidity: (control) => control.checkValidity(),
            reportValidity: (control) => control.reportValidity(),
            setValue: (control, val) => {
                const t = control;
                t._setFromFormValue(val);
            },
            assumeInteractionOn: ['it-change'],
        });
        this._sourceItems = [];
        this._targetItems = [];
        this._sourceChecked = new Set();
        this._targetChecked = new Set();
        /** Snapshot of the initial state for reset(). Set once on first slotchange. */
        this._initialSource = [];
        this._initialTarget = [];
    }
    // ── Form integration ──────────────────────────────────────────────────────
    get validity() {
        const valid = !this.required || this._targetItems.length > 0;
        return {
            valid,
            valueMissing: this.required && this._targetItems.length === 0,
            typeMismatch: false,
            patternMismatch: false,
            tooLong: false,
            tooShort: false,
            rangeUnderflow: false,
            rangeOverflow: false,
            stepMismatch: false,
            badInput: false,
            customError: this.customValidation && (this.validationText?.length ?? 0) > 0,
        };
    }
    checkValidity() {
        if (this.customValidation)
            return (this.validationText?.length ?? 0) === 0;
        return !this.required || this._targetItems.length > 0;
    }
    reportValidity() {
        const valid = this.checkValidity();
        if (!valid && !this.customValidation) {
            this.validationMessage = this.$t('validityGroupRequired');
        }
        else if (valid) {
            this.validationMessage = '';
        }
        this.formControlController.updateValidity();
        this.requestUpdate();
        return valid;
    }
    handleValidationMessages() {
        if (!this.customValidation && this.formControlController.submittedOnce) {
            this.validationMessage = this.checkValidity() ? '' : this.$t('validityGroupRequired');
        }
        if (this.customValidation) {
            this.validationMessage = this.validationText ?? '';
        }
    }
    _setFromFormValue(val) {
        if (!val)
            return;
        try {
            const targetValues = JSON.parse(val);
            const all = [...this._sourceItems, ...this._targetItems];
            this._targetItems = all.filter((i) => targetValues.includes(i.value));
            this._sourceItems = all.filter((i) => !targetValues.includes(i.value));
            this._sourceChecked = new Set();
            this._targetChecked = new Set();
            this._syncValue();
        }
        catch {
            // ignore malformed values
        }
    }
    _syncValue() {
        this.value = JSON.stringify(this._targetItems.map((i) => i.value));
        this.dispatchEvent(new CustomEvent('it-change', {
            detail: { value: this.value, el: this },
            bubbles: true,
            composed: true,
        }));
        this.handleValidationMessages();
        this.formControlController.updateValidity();
    }
    // ── Lifecycle ─────────────────────────────────────────────────────────────
    connectedCallback() {
        super.connectedCallback?.();
    }
    // ── Slot handling ─────────────────────────────────────────────────────────
    _handleSlotChange() {
        const items = this._slottedItems;
        const source = [];
        const target = [];
        for (const el of items) {
            const data = {
                value: el.value,
                label: el.textContent?.trim() ?? el.value,
                disabled: el.disabled,
            };
            if (el.target) {
                target.push(data);
            }
            else {
                source.push(data);
            }
        }
        // Only initialise once; on subsequent slot changes keep current state if values already exist
        if (this._initialSource.length === 0 && this._initialTarget.length === 0) {
            this._initialSource = source;
            this._initialTarget = target;
            this._sourceItems = source;
            this._targetItems = target;
            this._syncValue();
        }
    }
    // ── Actions ───────────────────────────────────────────────────────────────
    _dispatch(action, items, nextSource, nextTarget, defaultAction) {
        dispatchCancelable(this, 'it-transfer', {
            action,
            items,
            source: nextSource.map((i) => i.value),
            target: nextTarget.map((i) => i.value),
        }, defaultAction);
    }
    /** Move checked source items to the target list. */
    transfer() {
        const moving = this._sourceItems.filter((i) => this._sourceChecked.has(i.value) && !i.disabled);
        if (moving.length === 0)
            return;
        const nextSource = this._sourceItems.filter((i) => !this._sourceChecked.has(i.value));
        const nextTarget = [...this._targetItems, ...moving];
        this._dispatch('transfer', moving.map((i) => i.value), nextSource, nextTarget, () => {
            this._sourceItems = nextSource;
            this._targetItems = nextTarget;
            this._sourceChecked = new Set();
            this._syncValue();
        });
    }
    /** Move checked target items back to the source list. */
    backtransfer() {
        const moving = this._targetItems.filter((i) => this._targetChecked.has(i.value) && !i.disabled);
        if (moving.length === 0)
            return;
        const nextTarget = this._targetItems.filter((i) => !this._targetChecked.has(i.value));
        const nextSource = [...this._sourceItems, ...moving];
        this._dispatch('backtransfer', moving.map((i) => i.value), nextSource, nextTarget, () => {
            this._sourceItems = nextSource;
            this._targetItems = nextTarget;
            this._targetChecked = new Set();
            this._syncValue();
        });
    }
    /** Reset both lists to their initial state. */
    reset() {
        const nextSource = this._initialSource.slice();
        const nextTarget = this._initialTarget.slice();
        this._dispatch('reset', [], nextSource, nextTarget, () => {
            this._sourceItems = nextSource;
            this._targetItems = nextTarget;
            this._sourceChecked = new Set();
            this._targetChecked = new Set();
            this._syncValue();
        });
    }
    /**
     * Applies the proposed state from a previously cancelled `it-transfer` event,
     * without re-dispatching the event.
     *
     * Use this after calling `event.preventDefault()` in an `it-transfer` listener
     * to resume the action once your async logic (validation, confirmation, etc.) resolves:
     *
     * ```js
     * el.addEventListener('it-transfer', async (e) => {
     *   e.preventDefault();
     *   const ok = await confirm('Procedere con il trasferimento?');
     *   if (ok) el.commit(e.detail);
     * });
     * ```
     */
    commit(detail) {
        const known = new Map([...this._sourceItems, ...this._targetItems].map((i) => [i.value, i]));
        this._sourceItems = detail.source.flatMap((v) => (known.has(v) ? [known.get(v)] : []));
        this._targetItems = detail.target.flatMap((v) => (known.has(v) ? [known.get(v)] : []));
        this._sourceChecked = new Set();
        this._targetChecked = new Set();
        this._syncValue();
    }
    // ── Checkbox logic ────────────────────────────────────────────────────────
    _toggleItem(side, value, checked) {
        if (side === 'source') {
            const next = new Set(this._sourceChecked);
            if (checked)
                next.add(value);
            else
                next.delete(value);
            this._sourceChecked = next;
        }
        else {
            const next = new Set(this._targetChecked);
            if (checked)
                next.add(value);
            else
                next.delete(value);
            this._targetChecked = next;
        }
    }
    _toggleAll(side, selectAll) {
        if (side === 'source') {
            this._sourceChecked = selectAll
                ? new Set(this._sourceItems.filter((i) => !i.disabled).map((i) => i.value))
                : new Set();
        }
        else {
            this._targetChecked = selectAll
                ? new Set(this._targetItems.filter((i) => !i.disabled).map((i) => i.value))
                : new Set();
        }
    }
    // eslint-disable-next-line class-methods-use-this
    _getHeaderState(items, checked) {
        const selectableCount = items.filter((i) => !i.disabled).length;
        const checkedCount = items.filter((i) => checked.has(i.value)).length;
        return {
            allChecked: selectableCount > 0 && checkedCount === selectableCount,
            indeterminate: checkedCount > 0 && checkedCount < selectableCount,
            anyChecked: checkedCount > 0,
        };
    }
    // ── Render helpers ────────────────────────────────────────────────────────
    _renderList(side, items, checked, listId) {
        const headerState = this._getHeaderState(items, checked);
        const allDisabled = this.disabled || items.length === 0;
        const label = side === 'source'
            ? (this.sourceLabel ?? this.$t('transferSourceLabel'))
            : (this.targetLabel ?? this.$t('transferTargetLabel'));
        const count = this.$t('transferItemsCount').replace('{count}', items.length.toString());
        const headerId = `${this._id}-${side}-header`;
        return html `
      <div class="it-transfer-wrapper ${side}" part="${side}-wrapper" role="group" aria-labelledby="${headerId}">
        <div class="transfer-header" part="header">
          <div class="form-check">
            <input
              type="checkbox"
              id="${headerId}"
              class="it-form__control${headerState.indeterminate ? ' semi-checked' : ''}"
              .checked=${live(headerState.allChecked)}
              .indeterminate=${live(headerState.indeterminate)}
              .disabled=${allDisabled}
              aria-label="${this.$t('transferSelectAll')}"
              @change=${(e) => this._toggleAll(side, e.target.checked)}
            />
            <label for="${headerId}">
              <span class="num">${count}</span>
              <span class="descr">${label}</span>
            </label>
          </div>
        </div>
        <div class="transfer-scroll" part="scroll">
          <div class="transfer-group" id="${listId}" part="group">
            ${repeat(items, (item) => item.value, (item) => {
            const itemId = `${this._id}-${side}-${item.value}`;
            return html `
                  <div class="form-check">
                    <input
                      type="checkbox"
                      id="${itemId}"
                      class="it-form__control"
                      .checked=${checked.has(item.value)}
                      .disabled=${this.disabled || item.disabled || false}
                      @change=${(e) => this._toggleItem(side, item.value, e.target.checked)}
                    />
                    <label for="${itemId}">${item.label}</label>
                  </div>
                `;
        })}
          </div>
        </div>
      </div>
    `;
    }
    _renderButtons() {
        const hasSourceChecked = this._sourceChecked.size > 0;
        const hasTargetChecked = this._targetChecked.size > 0;
        const hasBeenModified = JSON.stringify(this._sourceItems.map((i) => i.value)) !== JSON.stringify(this._initialSource.map((i) => i.value));
        const btnTransferLabel = this.transferLabel ?? this.$t('transferLabel');
        const btnBackLabel = this.backtransferLabel ?? this.$t('transferBackLabel');
        const btnResetLabel = this.resetLabel ?? this.$t('transferResetLabel');
        return html `
      <div class="it-transfer-buttons" part="buttons">
        <a
          part="forward-button"
          role="button"
          tabindex="${hasSourceChecked && !this.disabled ? '0' : '-1'}"
          class="transfer ${hasSourceChecked && !this.disabled ? 'active' : ''}"
          aria-label="${btnTransferLabel}"
          ?disabled=${!hasSourceChecked || this.disabled}
          aria-disabled="${!hasSourceChecked || this.disabled ? 'true' : 'false'}"
          @click=${(e) => {
            e.preventDefault();
            this.transfer();
        }}
          @keydown=${(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.transfer();
            }
        }}
        >
          <it-icon name="it-arrow-right" size="sm"></it-icon>
        </a>
        <a
          part="back-button"
          role="button"
          tabindex="${hasTargetChecked && !this.disabled ? '0' : '-1'}"
          class="backtransfer ${hasTargetChecked && !this.disabled ? 'active' : ''}"
          aria-label="${btnBackLabel}"
          ?disabled=${!hasTargetChecked || this.disabled}
          aria-disabled="${!hasTargetChecked || this.disabled ? 'true' : 'false'}"
          @click=${(e) => {
            e.preventDefault();
            this.backtransfer();
        }}
          @keydown=${(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.backtransfer();
            }
        }}
        >
          <it-icon name="it-arrow-left" size="sm"></it-icon>
        </a>
        <a
          part="reset-button"
          role="button"
          tabindex="${hasBeenModified && !this.disabled ? '0' : '-1'}"
          class="reset ${hasBeenModified && !this.disabled ? 'active' : ''}"
          aria-label="${btnResetLabel}"
          ?disabled=${!hasBeenModified || this.disabled}
          aria-disabled="${!hasBeenModified || this.disabled ? 'true' : 'false'}"
          @click=${(e) => {
            e.preventDefault();
            this.reset();
        }}
          @keydown=${(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.reset();
            }
        }}
        >
          <it-icon name="it-restore" size="sm"></it-icon>
        </a>
      </div>
    `;
    }
    render() {
        const showValidation = this.formControlController.submittedOnce || this.customValidation;
        const validityMessage = (showValidation ? this.validationMessage : '') ?? '';
        const invalid = validityMessage.length > 0;
        const sourceListId = `${this._id}-source-list`;
        const targetListId = `${this._id}-target-list`;
        return html `
      <div class="it-transfer" part="base">
        <div class="row" part="row">
          <div class="col-12 col-md-5" part="source-col">
            ${this._renderList('source', this._sourceItems, this._sourceChecked, sourceListId)}
          </div>
          <div class="col-12 col-md-2 d-flex align-items-center justify-content-center" part="buttons-col">
            ${this._renderButtons()}
          </div>
          <div class="col-12 col-md-5" part="target-col">
            ${this._renderList('target', this._targetItems, this._targetChecked, targetListId)}
          </div>
        </div>

        ${invalid
            ? html `
              <div
                role="alert"
                id="invalid-feedback-${this._id}"
                class="invalid-feedback form-feedback form-text just-validate-error-label"
              >
                ${validityMessage}
              </div>
            `
            : ''}
      </div>

      <!-- Hidden slot to read it-transfer-item children -->
      <slot class="visually-hidden" aria-hidden="true" @slotchange=${this._handleSlotChange}></slot>
    `;
    }
};
ItTransfer.styles = styles;
__decorate([
    property({ type: String, attribute: 'source-label' }),
    __metadata("design:type", String)
], ItTransfer.prototype, "sourceLabel", void 0);
__decorate([
    property({ type: String, attribute: 'target-label' }),
    __metadata("design:type", String)
], ItTransfer.prototype, "targetLabel", void 0);
__decorate([
    property({ type: String, attribute: 'transfer-label' }),
    __metadata("design:type", String)
], ItTransfer.prototype, "transferLabel", void 0);
__decorate([
    property({ type: String, attribute: 'backtransfer-label' }),
    __metadata("design:type", String)
], ItTransfer.prototype, "backtransferLabel", void 0);
__decorate([
    property({ type: String, attribute: 'reset-label' }),
    __metadata("design:type", String)
], ItTransfer.prototype, "resetLabel", void 0);
__decorate([
    queryAssignedElements({ selector: 'it-transfer-item' }),
    __metadata("design:type", Array)
], ItTransfer.prototype, "_slottedItems", void 0);
__decorate([
    state(),
    __metadata("design:type", Array)
], ItTransfer.prototype, "_sourceItems", void 0);
__decorate([
    state(),
    __metadata("design:type", Array)
], ItTransfer.prototype, "_targetItems", void 0);
__decorate([
    state(),
    __metadata("design:type", Set)
], ItTransfer.prototype, "_sourceChecked", void 0);
__decorate([
    state(),
    __metadata("design:type", Set)
], ItTransfer.prototype, "_targetChecked", void 0);
ItTransfer = __decorate([
    customElement('it-transfer')
], ItTransfer);

export { ItTransfer };
//# sourceMappingURL=it-transfer.js.map
